import { useState, useRef, useCallback, useMemo, useEffect } from 'react'
import { Stage, Layer, Line, Circle, Group, Text, Arrow, Rect } from 'react-konva'
import { useTheme } from 'styled-components'
import {
  FiMousePointer,
  FiCircle,
  FiMinus,
  FiAnchor,
  FiArrowDown,
  FiTrendingDown,
  FiZoomIn,
  FiZoomOut,
  FiMaximize2,
  FiMaximize,
  FiMinimize,
  FiTrash2,
  FiCopy,
  FiActivity,
  FiMove,
  FiSave,
  FiUpload,
  FiPlay,
  FiLayers,
  FiTrendingUp,
  FiSliders,
  FiEye,
  FiEyeOff,
  FiTag,
  FiTool,
  FiBox,
  FiScissors,
} from 'react-icons/fi'
import {
  CanvasWrapper,
  Toolbar,
  ToolButton,
  ToolbarSeparator,
  ToolbarBreak,
  ToolbarSelect,
  HintBar,
  CanvasBody,
  StageHost,
  PropertiesPanel,
  PropertyRow,
  PropertyCheckboxRow,
  PropertiesEmptyState,
  NearbyList,
  NearbyItemButton,
} from './canvasStyles'
import {
  nextRowKey,
  createNode,
  createElement,
  createSupport,
  createNodalLoad,
  createElementLoad,
  cascadeRemoveNode,
  cascadeRemoveElement,
  splitElement,
  computeSectionProperties,
} from './model'
import { computeAllDiagrams, computeDeformedElementPoints, resolveElementLoadsForView } from './diagrams'
import {
  normalizeMovingResponses,
  buildStateDiagram,
  buildEnvelopeDiagram,
  buildNativeEnvelopeDiagram,
  mergeEnvelopeDiagrams,
  buildPathLayout,
  pathPositionToModelPoint,
} from './movingLoadsDiagrams'
import { roundToDecimals } from '../../../utils/numberFormat'
import { toast } from 'react-toastify'
import { Spinner } from '../../../components/Spinner'
import { FormErrorsBox } from './styles'
import { LibraryPanel } from './LibraryPanel'
import { MovingLoadsPanel } from './MovingLoadsPanel'
import { ViewOptionsPanel } from './ViewOptionsPanel'

const BASE_SCALE = 60
const GRID_EXTENT = 30
const DEFAULT_SNAP = 0.25
const DEFAULT_GRID_SPACING = 1
// Cap on grid lines per direction so a very fine spacing (e.g. 0.05 m over the 30 m extent) can't
// spawn thousands of Konva lines and stall the canvas; past this the grid simply stops covering the
// full extent instead of freezing.
const MAX_GRID_LINES = 160
const STAGE_HEIGHT = 480
// Zoom limits for the canvas. The upper bound is generous so small elements (e.g. 0.15 m stair
// steps) can be inspected up close; markers stay screen-constant via the 1/scale compensation.
const MIN_ZOOM = 0.2
const MAX_ZOOM = 16

// Next free `${prefix}${n}` id that isn't already taken (avoids colliding with existing ids when
// duplicating, since duplicate ids are flagged by validation).
function uniqueSequentialId(prefix, existingIds) {
  let n = existingIds.length + 1
  while (existingIds.includes(`${prefix}${n}`)) {
    n += 1
  }
  return `${prefix}${n}`
}

function snap(value, size) {
  // Snap is a free-form numeric input, so guard against a transient empty/zero/negative value
  // (which would make value/size NaN or Infinity) by falling back to the default step.
  const step = Number(size) > 0 ? Number(size) : DEFAULT_SNAP
  return Math.round(value / step) * step
}

// User-tunable view scales are free-form inputs; treat empty/zero/negative as the neutral 1x.
function positiveScale(value) {
  const n = Number(value)
  return n > 0 ? n : 1
}

function modelToWorld(x, y) {
  return { x: x * BASE_SCALE, y: -y * BASE_SCALE }
}

function worldToModel(x, y) {
  return { x: x / BASE_SCALE, y: -y / BASE_SCALE }
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max)
}

// Linear interpolation of a sampled curve (values over positions xs) at an arbitrary position.
// Used to read the frontend diagram's own value at the backend-reported extremum location, so the
// marker label keeps the same sign convention as the displayed N/V/M diagram.
function interpolateAt(xs, values, xTarget) {
  if (xTarget <= xs[0]) return values[0]
  if (xTarget >= xs[xs.length - 1]) return values[values.length - 1]
  for (let i = 1; i < xs.length; i += 1) {
    if (xTarget <= xs[i]) {
      const span = xs[i] - xs[i - 1] || 1
      const t = (xTarget - xs[i - 1]) / span
      return values[i - 1] + (values[i] - values[i - 1]) * t
    }
  }
  return values[values.length - 1]
}

// Compact label for a displacement/rotation value (they are often very small): exponential for
// tiny/huge magnitudes, otherwise ~4 significant digits with trailing zeros trimmed. `unit` only
// matters for linear displacements (u, v) — the model has no fixed length unit, but since the
// solver's own defaults and typical inputs treat coordinates as meters, "cm" scales the raw value
// by 100 for a more readable magnitude. Rotations (theta, in rad) are unitless and always passed
// with the default 'm' (i.e. left unscaled) by their callers.
function formatDisplacement(value, unit = 'm') {
  const raw = Number(value) || 0
  const n = unit === 'cm' ? raw * 100 : raw
  if (n === 0) return '0'
  const abs = Math.abs(n)
  if (abs < 1e-4 || abs >= 1e5) return n.toExponential(2)
  return String(Number(n.toPrecision(4)))
}

function pointToSegmentDistance(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1
  const dy = y2 - y1
  const lengthSq = dx * dx + dy * dy
  if (lengthSq === 0) return Math.hypot(px - x1, py - y1)
  const t = clamp(((px - x1) * dx + (py - y1) * dy) / lengthSq, 0, 1)
  const projX = x1 + t * dx
  const projY = y1 + t * dy
  return Math.hypot(px - projX, py - projY)
}

// Distance from a point to the nearest edge of an axis-aligned rect (0 if the point is inside).
// Used to let a click on a load's text label select it, approximating the label's footprint since
// Konva's actual measured text width isn't available outside the render pass.
function pointToRectDistance(px, py, rx, ry, rw, rh) {
  const dx = Math.max(rx - px, 0, px - (rx + rw))
  const dy = Math.max(ry - py, 0, py - (ry + rh))
  return Math.hypot(dx, dy)
}

// Rough label footprint for a load's text (see the Text elements rendered alongside load arrows):
// wide enough to cover most of the string at the given font size without needing Konva's actual
// text-measurement, which isn't available in the pointer-distance candidate search below.
function estimateLabelRect(text, x, y, fontSize) {
  return { x, y, width: text.length * fontSize * 0.56, height: fontSize * 1.3 }
}

// --- Marquee (rubber-band) selection geometry, all in world coordinates ---------------------
// A normalized selection rectangle from two opposite corners.
function normalizeRect(x0, y0, x1, y1) {
  return { minX: Math.min(x0, x1), minY: Math.min(y0, y1), maxX: Math.max(x0, x1), maxY: Math.max(y0, y1) }
}

function pointInRect(rect, x, y) {
  return x >= rect.minX && x <= rect.maxX && y >= rect.minY && y <= rect.maxY
}

// Signed area of the triangle (a, b, c); its sign gives the orientation of c relative to line a->b.
function orient(ax, ay, bx, by, cx, cy) {
  return (bx - ax) * (cy - ay) - (by - ay) * (cx - ax)
}

// Proper intersection of segments p1p2 and p3p4 (collinear/touching endpoints are ignored — good
// enough for picking, where an element grazing a corner either way is acceptable).
function segmentsIntersect(p1, p2, p3, p4) {
  const o1 = orient(p1.x, p1.y, p2.x, p2.y, p3.x, p3.y)
  const o2 = orient(p1.x, p1.y, p2.x, p2.y, p4.x, p4.y)
  const o3 = orient(p3.x, p3.y, p4.x, p4.y, p1.x, p1.y)
  const o4 = orient(p3.x, p3.y, p4.x, p4.y, p2.x, p2.y)
  return o1 > 0 !== o2 > 0 && o3 > 0 !== o4 > 0
}

// True when segment a-b touches the rect at all: either endpoint inside, or the segment crosses any
// of the four rect edges. Used for AutoCAD-style "crossing" (right-to-left) selection.
function segmentIntersectsRect(rect, a, b) {
  if (pointInRect(rect, a.x, a.y) || pointInRect(rect, b.x, b.y)) return true
  const c = [
    { x: rect.minX, y: rect.minY },
    { x: rect.maxX, y: rect.minY },
    { x: rect.maxX, y: rect.maxY },
    { x: rect.minX, y: rect.maxY },
  ]
  return (
    segmentsIntersect(a, b, c[0], c[1]) ||
    segmentsIntersect(a, b, c[1], c[2]) ||
    segmentsIntersect(a, b, c[2], c[3]) ||
    segmentsIntersect(a, b, c[3], c[0])
  )
}

function elementVectors(nodeI, nodeJ) {
  const dx = nodeJ.x - nodeI.x
  const dy = nodeJ.y - nodeI.y
  const length = Math.hypot(dx, dy) || 1
  const ux = dx / length
  const uy = dy / length
  return { ux, uy, nx: -uy, ny: ux, length }
}

function supportGlyphType(s) {
  if (s.restrainU && s.restrainV && s.restrainTheta) return 'fixed'
  if (s.restrainU && s.restrainV && !s.restrainTheta) return 'pinned'
  if (!s.restrainU && s.restrainV && !s.restrainTheta) return 'roller'
  return 'custom'
}

// Direction (in model space) pointing away from the member the support's node connects to,
// used to orient the "engaste" wall glyph perpendicular to whatever the member frames into.
function findAwaySupportDirection(nodeId, elements, nodesById) {
  const element = elements.find((e) => e.nodeI === nodeId || e.nodeJ === nodeId)
  const thisNode = nodesById[nodeId]
  if (!element || !thisNode) return { x: 0, y: -1 }

  const otherId = element.nodeI === nodeId ? element.nodeJ : element.nodeI
  const otherNode = nodesById[otherId]
  if (!otherNode) return { x: 0, y: -1 }

  const dx = thisNode.x - otherNode.x
  const dy = thisNode.y - otherNode.y
  const length = Math.hypot(dx, dy) || 1
  return { x: dx / length, y: dy / length }
}

const HINTS = {
  select:
    'Clique para selecionar/editar ou arraste um nó para movê-lo. Arraste da esquerda p/ direita seleciona o que estiver totalmente dentro (janela); da direita p/ esquerda, tudo que for tocado (interseção). Botão do meio move a vista.',
  node: 'Clique em um ponto vazio do canvas para criar um nó.',
  element: 'Clique em um nó inicial e depois em um nó final para criar um elemento.',
  support: 'Clique em um nó para adicionar/editar um apoio nele.',
  nodalLoad: 'Clique em um nó para adicionar/editar uma carga nodal nele.',
  elementLoad: 'Clique em um elemento para adicionar/editar uma carga nele.',
}

export function StructureCanvas({
  nodes,
  elements,
  supports,
  nodalLoads,
  elementLoads,
  materials,
  sections,
  loadCases,
  combinations,
  setNodes,
  setElements,
  setSupports,
  setNodalLoads,
  setElementLoads,
  setMaterials,
  setSections,
  setLoadCases,
  setCombinations,
  disabled,
  result,
  data,
  formErrors,
  isFormValid,
  canCalculate,
  onCalculate,
  onDesignAs,
  onCalculateEquivalentInertia,
  onSave,
  onFileUpload,
  movingLoadsPath,
  trains,
  setMovingLoadsPath,
  setTrains,
  movingLoadsResult,
  movingLoadsFormErrors,
}) {
  const theme = useTheme()
  const stageRef = useRef(null)
  const hostRef = useRef(null)
  const wrapperRef = useRef(null)
  const fileInputRef = useRef(null)
  // Marquee drag origin (world coords) while a rubber-band selection is in progress; a click that
  // barely moves is treated as a normal pick instead. `suppressClickRef` swallows the click event
  // Konva fires right after a real marquee drag so it doesn't immediately re-select a single item.
  const marqueeStartRef = useRef(null)
  const suppressClickRef = useRef(false)
  // Middle-mouse panning state (screen coords), so the canvas can still be panned in select mode
  // where left-drag is reserved for the marquee.
  const panRef = useRef(null)

  const [mode, setMode] = useState('select')
  const [scale, setScale] = useState(1)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [stagePos, setStagePos] = useState({ x: 260, y: 260 })
  const [selected, setSelected] = useState(null)
  // Values of the most recently typed nodal / element load: new loads (click on a node/element, or the
  // multi-node apply form) start from these instead of the factory defaults.
  const [lastNodalLoadValues, setLastNodalLoadValues] = useState({ Fx: 0, Fy: -10, Mz: 0 })
  const [lastElementLoadValues, setLastElementLoadValues] = useState(() => {
    const values = { ...createElementLoad('', '') }
    delete values.key
    delete values.element
    delete values.loadCase
    return values
  })
  // Number of equal segments the "Dividir" action splits the selected element into.
  const [splitParts, setSplitParts] = useState(2)
  // Multi-selection from a marquee: a list of { type: 'node' | 'element', key }. Kept separate from
  // the single `selected` (which drives the property editor); a marquee sets this and clears
  // `selected`, while any single pick clears this (see the effect below).
  const [multiSelection, setMultiSelection] = useState([])
  // Live marquee rectangle while dragging: { x0, y0, x1, y1 } in world coords, plus `crossing` when
  // the drag goes right-to-left (AutoCAD crossing vs. window semantics).
  const [marquee, setMarquee] = useState(null)
  const [nearbyCandidates, setNearbyCandidates] = useState([])
  const [pendingElementNode, setPendingElementNode] = useState(null)
  const [pendingDuplicate, setPendingDuplicate] = useState(null)
  // Offset (in meters) applied when duplicating the current selection by a fixed delta instead of
  // dropping copies at the cursor. Stored as strings so the inputs can be edited/cleared freely.
  const [duplicateDelta, setDuplicateDelta] = useState({ x: '1', y: '0' })
  const [stageWidth, setStageWidth] = useState(800)
  const [stageHeight, setStageHeight] = useState(STAGE_HEIGHT)
  const [diagramMode, setDiagramMode] = useState('none')
  // Grid/snap step for placing and dragging nodes, in meters. Small elements (e.g. a stair's
  // 0.15-0.30 m steps) need a finer grid than the 0.25 m default to be placeable by click at all.
  const [snapSize, setSnapSize] = useState(DEFAULT_SNAP)
  // Visual grid spacing in meters, independent per direction: `gridSpacingX` is the horizontal
  // distance between the vertical lines, `gridSpacingY` the vertical distance between the
  // horizontal lines. Kept separate from `snapSize` (the node placement step).
  const [gridSpacingX, setGridSpacingX] = useState(DEFAULT_GRID_SPACING)
  const [gridSpacingY, setGridSpacingY] = useState(DEFAULT_GRID_SPACING)
  // User multipliers (1 = default) for the on-screen size of each overlay family, so a busy or a
  // tiny model can be made readable. Stored as strings for free-form editing; `positiveScale`
  // coerces them at use. `deformed`/`diagram` scale the drawn amplitudes; `node`/`support` the
  // marker glyphs; `text` every label and diagram value.
  const [viewScales, setViewScales] = useState({ deformed: '1', diagram: '1', node: '1', support: '1', text: '1' })
  // Position (in meters from node I) at which to read N/V/M off the currently displayed
  // load case/combination — set either by clicking a diagram ribbon or typing into the
  // properties panel. Cleared once the referenced element stops having a diagram (model
  // edited, result cleared).
  const [queryPoint, setQueryPoint] = useState(null)
  const [showReactions, setShowReactions] = useState(true)
  const [showDisplacements, setShowDisplacements] = useState(false)
  const [displacementUnit, setDisplacementUnit] = useState('m')
  const [showLoads, setShowLoads] = useState(true)
  const [showNodeLabels, setShowNodeLabels] = useState(true)
  const [showElementLabels, setShowElementLabels] = useState(true)
  const [showLibrary, setShowLibrary] = useState(false)
  const [showMovingLoads, setShowMovingLoads] = useState(false)
  const [showViewOptions, setShowViewOptions] = useState(false)
  const [movingDiagramMode, setMovingDiagramMode] = useState('none')
  const [movingStateSelection, setMovingStateSelection] = useState('envelope')
  // Which train's movingResponse is active (when several trains were solved), and which source
  // within it drives the diagrams: 'train' for the pure train, or a combination id for one of that
  // train's moving-load combinations (see HANDOFF.md).
  const [movingResponseId, setMovingResponseId] = useState(null)
  const [movingSource, setMovingSource] = useState('train')
  const [analysisView, setAnalysisView] = useState('static')
  const [selectedView, setSelectedView] = useState(null)
  const [activeLoadCase, setActiveLoadCase] = useState(loadCases[0]?.id || '')

  useEffect(() => {
    if (loadCases.length === 0) {
      if (activeLoadCase !== '') setActiveLoadCase('')
      return
    }
    if (!loadCases.some((c) => c.id === activeLoadCase)) setActiveLoadCase(loadCases[0].id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadCases])

  // The backend always responds with results grouped by load case / combination (see HANDOFF.md);
  // this page picks one "view" at a time to drive the reactions overlay and N/V/M diagrams.
  const resultViews = useMemo(() => {
    const caseViews = (result?.loadCases || []).map((lc) => ({ type: 'case', id: lc.id, label: lc.name || lc.id }))
    const combinationViews = (result?.combinations || []).map((c) => ({
      type: 'combination',
      id: c.id,
      label: c.name || c.id,
    }))
    return [...caseViews, ...combinationViews]
  }, [result])

  useEffect(() => {
    if (resultViews.length === 0) {
      if (selectedView !== null) setSelectedView(null)
      // Results were cleared (e.g. the model was edited) — reset the canvas to "Sem diagrama".
      if (diagramMode !== 'none') setDiagramMode('none')
      return
    }
    const stillValid = resultViews.some((v) => v.type === selectedView?.type && v.id === selectedView?.id)
    if (!stillValid) setSelectedView(resultViews[0])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultViews])

  // Any single pick (click on a shape, placement, nearby-item button) sets `selected` to a truthy
  // value — that supersedes a marquee selection, so drop it. A marquee itself sets `selected` to
  // null, so it never trips this.
  useEffect(() => {
    if (selected) setMultiSelection([])
  }, [selected])

  const selectedResult = useMemo(() => {
    if (!selectedView) return null
    const list = selectedView.type === 'case' ? result?.loadCases : result?.combinations
    return list?.find((v) => v.id === selectedView.id) || null
  }, [result, selectedView])

  const hasResult = Boolean(selectedResult)

  // Shared /concrete/bending payload shape once a design moment (in kN·m) has already been
  // extracted and vetted — b/h on a rectangular section and bf/hf/bw/h on a T section are all
  // already in cm (see model.js).
  function buildDesignAsBasePayload(section, material, momentMag) {
    const base = {
      fck: Number(material.fck) || 0,
      fyk: 500, // CA-50 — aço não é modelado por material ainda, então assume o valor mais comum
      dlb: Number(section.rebarDistanceBottom) || 0,
      dlt: Number(section.rebarDistanceTop) || 0,
      md: momentMag * 100, // kN·m -> kN·cm
      rhoMin: 0.15,
      distribution: 'beam',
    }

    if (section.shape === 'tSection') {
      return {
        ...base,
        tShape: true,
        bw: Number(section.bw) || 0,
        bf: Number(section.bf) || 0,
        hf: Number(section.hf) || 0,
        h: Number(section.h) || 0,
      }
    }

    return { ...base, tShape: false, b: Number(section.b) || 0, h: Number(section.h) || 0 }
  }

  // Builds the /concrete/bending payload for the "Dimensionar As" action on an element in the
  // static analysis, or null when it isn't eligible: needs a concrete section that's rectangular
  // or T (the endpoint doesn't support I or custom sections), a calculated result for the current
  // view, a nonzero design moment, and a near-zero axial force — the endpoint models pure bending,
  // not combined bending + axial.
  function buildDesignAsPayload(el, section, material) {
    if (!section || material?.materialType !== 'concrete') return null
    if (section.shape !== 'rectangular' && section.shape !== 'tSection') return null
    const elementResult = selectedResult?.elements?.find((r) => r.id === el.id)
    const moment = elementResult?.internalExtrema?.forces?.moment
    const axial = elementResult?.internalExtrema?.forces?.axial
    if (!moment || !axial) return null
    const momentMag = Math.abs(moment.value)
    if (momentMag < 1e-9) return null
    if (Math.abs(axial.value) > 1e-6) return null

    return buildDesignAsBasePayload(section, material, momentMag)
  }

  // Same eligibility/payload as buildDesignAsPayload, but for the moving-load analysis: the design
  // moment is the worst case (largest |M|) anywhere along the element across the whole envelope
  // (both max and min curves), not just the state currently on screen — dimensioning must cover the
  // full sweep of the train, not one reference position. The axial check is likewise the worst case
  // across the envelope, so a member that only stays near-zero axial for part of the sweep is still
  // excluded from this pure-bending endpoint.
  function buildMovingDesignAsPayload(el, section, material, envelopeInfo) {
    if (!section || material?.materialType !== 'concrete') return null
    if (section.shape !== 'rectangular' && section.shape !== 'tSection') return null
    const diagram = envelopeInfo?.diagramsByElementId?.[el.id]
    if (!diagram) return null
    const momentValues = [...(diagram.max?.M || []), ...(diagram.min?.M || [])]
    if (momentValues.length === 0) return null
    const momentMag = Math.max(...momentValues.map((v) => Math.abs(v)))
    if (!Number.isFinite(momentMag) || momentMag < 1e-9) return null
    const axialValues = [...(diagram.max?.N || []), ...(diagram.min?.N || [])]
    const axialMag = axialValues.length > 0 ? Math.max(...axialValues.map((v) => Math.abs(v))) : 0
    if (axialMag > 1e-6) return null

    return buildDesignAsBasePayload(section, material, momentMag)
  }

  function handleDesignAsClick(el, payload) {
    if (!payload.fck) {
      toast.error('Defina o fck do material antes de dimensionar a armadura.')
      return
    }
    if (!payload.dlb) {
      toast.error('Defina a distância da armadura inferior à face na seção antes de dimensionar.')
      return
    }
    onDesignAs(el.key, payload)
  }

  // Suggests alpha_e (Es/Ecs) from fck per NBR 6118, assuming alpha_E = 1 (granito/gnaisse) — same
  // formula as the Inércia Equivalente calculator's own fck field (see BransonInertia/index.js).
  function suggestAlphaEFromFck(fckValue) {
    const fckNum = Number(fckValue)
    if (!fckNum || fckNum <= 0) return null
    const eci = fckNum <= 50 ? 5600 * Math.sqrt(fckNum) : 21500 * (fckNum / 10 + 1.25) ** (1 / 3)
    const alphaI = Math.min(0.8 + 0.2 * (fckNum / 80), 1)
    const ecs = alphaI * eci
    if (!ecs) return null
    return 210000 / ecs
  }

  // Builds the /concrete/branson-inertia payload for the "Calcular inércia equivalente" action on
  // an element — same eligibility as buildDesignAsPayload but also allows invertedTSection (Branson
  // supports it; /concrete/flexao doesn't), plus a requirement that As has already been dimensioned
  // (Branson's Ah/Ih need the actual reinforcement area). b/h and bf/hf/bw/h are all already in cm
  // (see model.js). Field shape matches how /concreto/inercia-equivalente calls this endpoint (see
  // BransonInertia/index.js's handleCalculate): a sectionType string, not a tShape boolean.
  function buildEquivalentInertiaPayload(el, section, material) {
    if (!section || material?.materialType !== 'concrete') return null
    if (!['rectangular', 'tSection', 'invertedTSection'].includes(section.shape)) return null
    const elementResult = selectedResult?.elements?.find((r) => r.id === el.id)
    const moment = elementResult?.internalExtrema?.forces?.moment
    const axial = elementResult?.internalExtrema?.forces?.axial
    if (!moment || !axial) return null
    const momentMag = Math.abs(moment.value)
    if (momentMag < 1e-9) return null
    if (Math.abs(axial.value) > 1e-6) return null
    const asValue = Number(el.as) || 0
    if (asValue <= 0) return null

    const hCm = Number(section.h) || 0
    const dlbCm = Number(section.rebarDistanceBottom) || 0
    const sectionType =
      section.shape === 'tSection' ? 't' : section.shape === 'invertedTSection' ? 't-invertido' : 'rectangular'

    const base = {
      fck: Number(material.fck) || 0,
      alphae: suggestAlphaEFromFck(material.fck) || 0,
      sectionType,
      h: hCm,
      d: hCm - dlbCm,
      as: asValue,
      asPrime: Number(el.asPrime) || 0,
      dPrime: Number(section.rebarDistanceTop) || 0,
      ma: momentMag * 100, // kN·m -> kN·cm
    }

    if (sectionType === 'rectangular') {
      const bCm = Number(section.b) || 0
      return { ...base, bf: bCm, bw: bCm, hf: hCm }
    }

    return {
      ...base,
      bf: Number(section.bf) || 0,
      bw: Number(section.bw) || 0,
      hf: Number(section.hf) || 0,
    }
  }

  function handleEquivalentInertiaClick(section, payload) {
    if (!payload.fck) {
      toast.error('Defina o fck do material antes de calcular a inércia equivalente.')
      return
    }
    if (payload.d <= 0) {
      toast.error('Defina a distância da armadura inferior à face na seção antes de calcular a inércia equivalente.')
      return
    }
    onCalculateEquivalentInertia(section, payload)
  }

  const diagramInfo = useMemo(() => {
    if (!hasResult) return null
    const loadsForView = resolveElementLoadsForView(data, selectedView)
    // Node coordinates let the reconstruction project global-coordinate element loads onto each
    // member's local axes (nodesById is declared later in this component, so build the map here).
    const nodeMap = {}
    nodes.forEach((n) => {
      nodeMap[n.id] = n
    })
    return computeAllDiagrams(elements, selectedResult.elements, loadsForView, nodeMap)
  }, [hasResult, elements, selectedResult, data, selectedView, nodes])

  useEffect(() => {
    if (queryPoint && !diagramInfo?.diagramsByElementId?.[queryPoint.elementId]) setQueryPoint(null)
  }, [diagramInfo, queryPoint])

  const displacementByNode = useMemo(() => {
    const map = {}
    if (hasResult) {
      ;(selectedResult.displacements || []).forEach((d) => {
        map[d.node] = d
      })
    }
    return map
  }, [hasResult, selectedResult])

  // Auto-scale so the largest nodal displacement reads as ~8% of the structure's size, then build
  // each element's deflected polyline in world coordinates for the overlay.
  const deformedShape = useMemo(() => {
    if (!hasResult || !selectedResult.elements) return null

    const byId = {}
    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity
    nodes.forEach((n) => {
      byId[n.id] = n
      minX = Math.min(minX, n.x)
      maxX = Math.max(maxX, n.x)
      minY = Math.min(minY, n.y)
      maxY = Math.max(maxY, n.y)
    })
    const diag = Math.hypot(maxX - minX, maxY - minY) || 1

    // Nodal u/v alone miss cases where both nodes are translationally restrained (u = v = 0 at
    // both ends) but the member still bows out in the interior purely from nodal rotations —
    // internalExtrema.displacement.transverse is the actual peak deflection in that case, and
    // must factor into the scale or the whole deformed shape would be multiplied by a zero scale.
    let maxDisp = 0
    ;(selectedResult.displacements || []).forEach((d) => {
      maxDisp = Math.max(maxDisp, Math.hypot(Number(d.u) || 0, Number(d.v) || 0))
    })
    ;(selectedResult.elements || []).forEach((elementResult) => {
      const transverse = elementResult.internalExtrema?.displacement?.transverse?.value
      if (transverse !== undefined && transverse !== null) {
        maxDisp = Math.max(maxDisp, Math.abs(transverse))
      }
    })
    const scale = (maxDisp > 0 ? (0.08 * diag) / maxDisp : 0) * positiveScale(viewScales.deformed)

    const polylines = []
    elements.forEach((el) => {
      const nodeI = byId[el.nodeI]
      const nodeJ = byId[el.nodeJ]
      if (!nodeI || !nodeJ) return
      const elementResult = selectedResult.elements.find((r) => r.id === el.id)
      if (!elementResult?.localDisplacements) return
      const ext = elementResult.internalExtrema?.displacement?.transverse
      const modelPoints = computeDeformedElementPoints(nodeI, nodeJ, elementResult.localDisplacements, scale, {
        moment: diagramInfo?.diagramsByElementId?.[el.id],
        transverseExt: ext,
      })
      const worldPoints = modelPoints.map((p) => modelToWorld(p.x, p.y))
      const flat = worldPoints.flatMap((w) => [w.x, w.y])

      // Mark the peak transverse deflection between the nodes (the whole point of internalExtrema):
      // it's consistent with the deformed curve since both come from the same local displacements.
      let marker = null
      if (ext && Number.isFinite(ext.xi) && ext.xi > 0.02 && ext.xi < 0.98) {
        const idx = ext.xi * (worldPoints.length - 1)
        const i0 = Math.floor(idx)
        const a = worldPoints[i0]
        const b = worldPoints[Math.min(i0 + 1, worldPoints.length - 1)]
        const frac = idx - i0
        marker = { x: a.x + (b.x - a.x) * frac, y: a.y + (b.y - a.y) * frac, value: ext.value }
      }

      polylines.push({ id: el.id, key: el.key, points: flat, marker })
    })

    return { polylines, scale }
  }, [hasResult, selectedResult, elements, nodes, diagramInfo, viewScales.deformed])

  // A ResizeObserver (rather than window 'resize'/'fullscreenchange' listeners) is what's needed
  // here: entering/exiting fullscreen fires 'fullscreenchange' before the browser finishes
  // recomputing layout, so measuring the host element's size at that moment reads the stale,
  // pre-transition size (only fixed by an unrelated event like alt-tabbing back into the tab,
  // which happens to force a layout flush). ResizeObserver instead fires once the element's
  // actual box size has changed, whatever the cause.
  useEffect(() => {
    const host = hostRef.current
    if (!host) return

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) return
      const { width, height } = entry.contentRect
      setStageWidth(width)
      setStageHeight(height)
    })

    observer.observe(host)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    function handleFullscreenChange() {
      setIsFullscreen(document.fullscreenElement === wrapperRef.current)
    }
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [])

  function toggleFullscreen() {
    if (document.fullscreenElement) {
      document.exitFullscreen()
    } else {
      wrapperRef.current?.requestFullscreen()
    }
  }

  function selectMode(nextMode) {
    setMode(nextMode)
    setPendingElementNode(null)
    setPendingDuplicate(null)
    setSelected(null)
    setMultiSelection([])
    setNearbyCandidates([])
  }

  function zoomBy(factor) {
    setScale((prev) => clamp(prev * factor, MIN_ZOOM, MAX_ZOOM))
  }

  function resetView() {
    setScale(1)
    setStagePos({ x: 260, y: 260 })
  }

  const handleWheel = useCallback((e) => {
    e.evt.preventDefault()
    const stage = stageRef.current
    if (!stage) return
    const oldScale = stage.scaleX()
    const pointer = stage.getPointerPosition()
    if (!pointer) return
    const mousePointTo = {
      x: (pointer.x - stage.x()) / oldScale,
      y: (pointer.y - stage.y()) / oldScale,
    }
    const direction = e.evt.deltaY > 0 ? -1 : 1
    const newScale = clamp(oldScale * (direction > 0 ? 1.08 : 0.93), MIN_ZOOM, MAX_ZOOM)
    setScale(newScale)
    setStagePos({
      x: pointer.x - mousePointTo.x * newScale,
      y: pointer.y - mousePointTo.y * newScale,
    })
  }, [])

  function handleStageClick(e) {
    if (disabled) return

    // Konva fires a click right after a marquee drag ends; ignore that one so the drag's selection
    // isn't immediately replaced by a single pick at the release point.
    if (suppressClickRef.current) {
      suppressClickRef.current = false
      return
    }

    const stage = stageRef.current
    const pointer = stage.getRelativePointerPosition()

    // A click while carrying a copy drops it at the (snapped) cursor, wherever that is.
    if (pendingDuplicate) {
      const dropModel = worldToModel(pointer.x, pointer.y)
      placeDuplicate(pendingDuplicate, snap(dropModel.x, snapSize), snap(dropModel.y, snapSize))
      return
    }

    if (mode === 'select') {
      const candidates = findSelectableCandidates(pointer)
      setNearbyCandidates(candidates)
      setSelected(candidates[0] || null)
      // A plain click supersedes a marquee selection, even when it hits empty canvas (which leaves
      // `selected` null, so the clear-on-select effect wouldn't fire).
      setMultiSelection([])
      return
    }

    if (e.target !== e.target.getStage()) return

    const model = worldToModel(pointer.x, pointer.y)
    const sx = snap(model.x, snapSize)
    const sy = snap(model.y, snapSize)

    if (mode === 'node') {
      const newNode = createNode(nodes, sx, sy)
      setNodes((prev) => [...prev, newNode])
      setSelected({ type: 'node', key: newNode.key })
    } else if (mode === 'element') {
      setPendingElementNode(null)
    }
  }

  // Mouse-down starts either a middle-button pan (any mode) or, in select mode on empty canvas, a
  // marquee selection. Picks on a shape (target !== stage) are left to their own click handlers.
  function handleStageMouseDown(e) {
    if (disabled) return
    // Start each gesture with a clean slate so a click that never fired (e.g. drag canceled) can't
    // leave the suppress flag set and swallow the next legitimate click.
    suppressClickRef.current = false
    const stage = stageRef.current
    if (e.evt && e.evt.button === 1) {
      e.evt.preventDefault()
      const pointer = stage.getPointerPosition()
      panRef.current = { pointer, origin: { ...stagePos } }
      return
    }
    if (mode !== 'select' || pendingDuplicate) return
    if (e.target !== stage) return
    const p = stage.getRelativePointerPosition()
    marqueeStartRef.current = { x: p.x, y: p.y }
  }

  function handleStageMouseMove() {
    const stage = stageRef.current
    if (!stage) return

    // Middle-button panning: follow the cursor by the screen-space delta since the drag began.
    if (panRef.current) {
      const pointer = stage.getPointerPosition()
      const { pointer: start, origin } = panRef.current
      setStagePos({ x: origin.x + (pointer.x - start.x), y: origin.y + (pointer.y - start.y) })
      return
    }

    // While a copy is being carried, keep its ghost anchored to the (snapped) cursor.
    if (pendingDuplicate) {
      const pointer = stage.getRelativePointerPosition()
      if (!pointer) return
      const model = worldToModel(pointer.x, pointer.y)
      const x = snap(model.x, snapSize)
      const y = snap(model.y, snapSize)
      setPendingDuplicate((prev) => {
        if (!prev || (prev.pos.x === x && prev.pos.y === y)) return prev
        return { ...prev, pos: { x, y } }
      })
      return
    }

    // Growing marquee rectangle. `crossing` (right-to-left) picks anything the box touches; a
    // left-to-right box (window) picks only fully-enclosed items.
    if (marqueeStartRef.current) {
      const p = stage.getRelativePointerPosition()
      const start = marqueeStartRef.current
      setMarquee({ x0: start.x, y0: start.y, x1: p.x, y1: p.y, crossing: p.x < start.x })
    }
  }

  // Human-readable label for an item in a multi-selection (node/element/support — a marquee never
  // picks loads), so the list below the "N itens selecionados" summary can show what's actually
  // selected instead of just a count.
  function describeMultiSelectionItem(item) {
    if (item.type === 'node') {
      const node = nodes.find((n) => n.key === item.key)
      return node ? `Nó ${node.id}` : 'Nó'
    }
    if (item.type === 'element') {
      const el = elements.find((e) => e.key === item.key)
      return el ? `Elemento ${el.id}` : 'Elemento'
    }
    if (item.type === 'support') {
      const support = supports.find((s) => s.key === item.key)
      return support ? `Apoio em ${support.node}` : 'Apoio'
    }
    return item.type
  }

  // Sets the current selection from a list of picked items (marquee drag, repeat-duplicate of a
  // marquee selection): a single item behaves exactly like a plain click-to-select instead of
  // staying a one-item "multi selection" with its own, different properties panel.
  function applySelection(items) {
    if (items.length === 1) {
      setSelected(items[0])
      setMultiSelection([])
    } else {
      setSelected(null)
      setMultiSelection(items)
    }
  }

  function handleStageMouseUp() {
    if (panRef.current) {
      panRef.current = null
      return
    }
    if (!marqueeStartRef.current) return
    const start = marqueeStartRef.current
    marqueeStartRef.current = null
    setMarquee(null)

    const stage = stageRef.current
    const end = stage?.getRelativePointerPosition()
    if (!end) return

    // A negligible drag is really a click — let the click handler pick a single item instead.
    const movedEnough = Math.hypot(end.x - start.x, end.y - start.y) > 3 / scale
    if (!movedEnough) return

    suppressClickRef.current = true
    const rect = normalizeRect(start.x, start.y, end.x, end.y)
    const crossing = end.x < start.x
    const picked = []
    nodes.forEach((n) => {
      const p = modelToWorld(n.x, n.y)
      if (pointInRect(rect, p.x, p.y)) picked.push({ type: 'node', key: n.key })
    })
    elements.forEach((el) => {
      const nodeI = nodesById[el.nodeI]
      const nodeJ = nodesById[el.nodeJ]
      if (!nodeI || !nodeJ) return
      const a = modelToWorld(nodeI.x, nodeI.y)
      const b = modelToWorld(nodeJ.x, nodeJ.y)
      const hit = crossing
        ? segmentIntersectsRect(rect, a, b)
        : pointInRect(rect, a.x, a.y) && pointInRect(rect, b.x, b.y)
      if (hit) picked.push({ type: 'element', key: el.key })
    })
    supports.forEach((s) => {
      const node = nodesById[s.node]
      if (!node) return
      const p = modelToWorld(node.x, node.y)
      if (pointInRect(rect, p.x, p.y)) picked.push({ type: 'support', key: s.key })
    })
    setNearbyCandidates([])
    applySelection(picked)
  }

  // In select mode, clicks are resolved by findClosestSelectable (nearest-shape hit test) via
  // handleStageClick instead of Konva's own topmost-shape hit detection — this lets the click
  // bubble up to the stage rather than being captured by whichever overlapping shape happens to
  // be on top, which previously made it hard to pick between e.g. a load arrow and the beam
  // underneath it.
  function handleNodeClick(node, e) {
    if (disabled) return
    if (mode === 'select') return
    e.cancelBubble = true

    if (mode === 'element') {
      if (!pendingElementNode) {
        setPendingElementNode(node.key)
      } else if (pendingElementNode === node.key) {
        setPendingElementNode(null)
      } else {
        const startNode = nodes.find((n) => n.key === pendingElementNode)
        const newElement = createElement(elements, startNode.id, node.id, sections[0]?.id || '')
        setElements((prev) => [...prev, newElement])
        // Keep drawing: the end node becomes the start of the next element (a polyline chain). The
        // chain ends with a click on empty canvas, on the pending node again, Esc or the space bar.
        setPendingElementNode(node.key)
        setSelected({ type: 'element', key: newElement.key })
      }
    } else if (mode === 'support') {
      let support = supports.find((s) => s.node === node.id)
      if (!support) {
        support = createSupport(node.id)
        setSupports((prev) => [...prev, support])
      }
      setSelected({ type: 'support', key: support.key })
    } else if (mode === 'nodalLoad') {
      let load = nodalLoads.find((l) => l.node === node.id && l.loadCase === activeLoadCase)
      if (!load) {
        load = { ...createNodalLoad(node.id, activeLoadCase), ...lastNodalLoadValues }
        setNodalLoads((prev) => [...prev, load])
      }
      setSelected({ type: 'nodalLoad', key: load.key })
    }
  }

  function handleElementClick(el, e) {
    if (disabled) return
    if (mode === 'select') return
    e.cancelBubble = true

    if (mode === 'elementLoad') {
      let load = elementLoads.find((l) => l.element === el.id && l.loadCase === activeLoadCase)
      if (!load) {
        load = { ...createElementLoad(el.id, activeLoadCase), ...lastElementLoadValues }
        setElementLoads((prev) => [...prev, load])
      }
      setSelected({ type: 'elementLoad', key: load.key })
    }
  }

  // Projects a click on a diagram ribbon onto the element's axis to read off a position (in
  // meters from node I), then selects the element and stores that position so the properties
  // panel can show the interpolated N/V/M there.
  function handleDiagramQueryClick(el, nodeI, nodeJ, e) {
    if (disabled) return
    e.cancelBubble = true
    const stage = e.target.getStage()
    const pointer = stage.getRelativePointerPosition()
    const model = worldToModel(pointer.x, pointer.y)
    const { ux, uy, length } = elementVectors(nodeI, nodeJ)
    const x = clamp((model.x - nodeI.x) * ux + (model.y - nodeI.y) * uy, 0, length)
    setSelected({ type: 'element', key: el.key })
    setQueryPoint({ elementId: el.id, x })
  }

  function handleSupportClick(support, e) {
    if (disabled) return
    if (mode === 'select') return
    e.cancelBubble = true
    setSelected({ type: 'support', key: support.key })
  }

  function handleElementLoadClick(load, e) {
    if (disabled) return
    if (mode === 'select') return
    e.cancelBubble = true
    setSelected({ type: 'elementLoad', key: load.key })
  }

  function handleNodeDragEnd(node, e) {
    const pos = e.target.position()
    const model = worldToModel(pos.x, pos.y)
    const sx = snap(model.x, snapSize)
    const sy = snap(model.y, snapSize)
    setNodes((prev) => prev.map((n) => (n.key === node.key ? { ...n, x: sx, y: sy } : n)))
  }

  // Finds every selectable item (node, element, support, or load) within click range, sorted by
  // distance to the click point, instead of relying on Konva's topmost-shape hit detection (which
  // arbitrarily favors whichever shape happens to be drawn last when several overlap, e.g. a load
  // arrow anchored right on a beam). The closest one is selected immediately; the rest are offered
  // as a "nearby items" list so the user can pick a different one without having to click again.
  function findSelectableCandidates(pointer) {
    const threshold = 14 / scale
    const candidates = []
    // Glyphs are drawn at screen-constant offsets (multiplied by k = 1/scale), so the hit test must
    // use the same k for its anchor offsets — otherwise the clickable region drifts away from the
    // drawn shape at any zoom ≠ 1. supportScaleF matches the support-glyph view scale.
    const k = 1 / scale
    const supportScaleF = positiveScale(viewScales.support)

    function consider(distance, candidate, customThreshold) {
      if (distance < (customThreshold ?? threshold)) {
        candidates.push({ ...candidate, distance })
      }
    }

    nodes.forEach((n) => {
      const p = modelToWorld(n.x, n.y)
      consider(Math.hypot(pointer.x - p.x, pointer.y - p.y), { type: 'node', key: n.key, label: `Nó ${n.id}` })
    })

    elements.forEach((el) => {
      const nodeI = nodesById[el.nodeI]
      const nodeJ = nodesById[el.nodeJ]
      if (!nodeI || !nodeJ) return
      const pI = modelToWorld(nodeI.x, nodeI.y)
      const pJ = modelToWorld(nodeJ.x, nodeJ.y)
      const distance = pointToSegmentDistance(pointer.x, pointer.y, pI.x, pI.y, pJ.x, pJ.y)
      consider(distance, { type: 'element', key: el.key, label: `Elemento ${el.id}` })
    })

    supports.forEach((s) => {
      const node = nodesById[s.node]
      if (!node) return
      const p = modelToWorld(node.x, node.y)
      // The glyph hangs below the node (apex ~2, base/hatches ~22-31 px); anchor the hit near its
      // centroid and use a threshold that grows with the glyph so a scaled-up support stays easy to
      // click.
      const anchorY = p.y + 16 * k * supportScaleF
      const distance = Math.hypot(pointer.x - p.x, pointer.y - anchorY)
      const supportThreshold = (16 * Math.max(1, supportScaleF)) / scale
      consider(distance, { type: 'support', key: s.key, label: `Apoio em ${s.node}` }, supportThreshold)
    })

    nodalLoads.forEach((l) => {
      if (!showLoads) return
      const node = nodesById[l.node]
      if (!node) return
      const tip = modelToWorld(node.x, node.y)
      const fx = Number(l.Fx) || 0
      const fy = Number(l.Fy) || 0
      const mag = Math.hypot(fx, fy)
      let distance
      if (mag === 0) {
        distance = Math.hypot(pointer.x - tip.x, pointer.y - (tip.y - 18 * k))
      } else {
        const screenDir = { x: fx / mag, y: -(fy / mag) }
        const tail = { x: tip.x - screenDir.x * 45 * k, y: tip.y - screenDir.y * 45 * k }
        distance = pointToSegmentDistance(pointer.x, pointer.y, tail.x, tail.y, tip.x, tip.y)
      }
      consider(distance, { type: 'nodalLoad', key: l.key, label: `Carga nodal em ${l.node} [${l.loadCase}]` })
    })

    // Mirrors the stacking done when drawing element loads (see the elementLoads render below):
    // loads sharing an element are offset further out one after another, so the hit test has to
    // walk them in the same order to know each one's actual on-screen position.
    const elementLoadStackCount = {}
    const fontSize = 11 * k * positiveScale(viewScales.text)

    elementLoads.forEach((l) => {
      if (!showLoads) return
      const el = elements.find((e) => e.id === l.element)
      if (!el) return
      const nodeI = nodesById[el.nodeI]
      const nodeJ = nodesById[el.nodeJ]
      if (!nodeI || !nodeJ) return
      const stackIndex = elementLoadStackCount[l.element] || 0
      elementLoadStackCount[l.element] = stackIndex + 1
      const { ux, uy, nx, ny, length } = elementVectors(nodeI, nodeJ)
      const axialDir = l.coordinateSystem === 'global' ? { x: 1, y: 0 } : { x: ux, y: uy }
      const perpDir = l.coordinateSystem === 'global' ? { x: 0, y: 1 } : { x: nx, y: ny }
      let distance = Infinity

      if (l.type === 'point') {
        const t = clamp(Number(l.position) || 0, 0, length) / length
        const baseX = nodeI.x + (nodeJ.x - nodeI.x) * t
        const baseY = nodeI.y + (nodeJ.y - nodeI.y) * t
        const vx = axialDir.x * (Number(l.Px) || 0) + perpDir.x * (Number(l.Py) || 0)
        const vy = axialDir.y * (Number(l.Px) || 0) + perpDir.y * (Number(l.Py) || 0)
        const mag = Math.hypot(vx, vy)
        const dir = mag > 0 ? { x: vx / mag, y: vy / mag } : { x: 0, y: -1 }
        const tip = modelToWorld(baseX, baseY)
        const screenDir = { x: dir.x, y: -dir.y }
        const tailDist = (45 + stackIndex * 22) * k
        const tail = { x: tip.x - screenDir.x * tailDist, y: tip.y - screenDir.y * tailDist }
        distance = pointToSegmentDistance(pointer.x, pointer.y, tail.x, tail.y, tip.x, tip.y)

        const text = `Px=${l.Px} Py=${l.Py}${Number(l.Mz) ? ` Mz=${l.Mz}` : ''} [${l.loadCase}]`
        const labelRect = estimateLabelRect(text, tail.x - 10 * k, tail.y - 16 * k, fontSize)
        distance = Math.min(
          distance,
          pointToRectDistance(pointer.x, pointer.y, labelRect.x, labelRect.y, labelRect.width, labelRect.height)
        )
      } else {
        const qxAvg = ((Number(l.qxI) || 0) + (Number(l.qxJ) || 0)) / 2
        const qyAvg = ((Number(l.qyI) || 0) + (Number(l.qyJ) || 0)) / 2
        const vx = axialDir.x * qxAvg + perpDir.x * qyAvg
        const vy = axialDir.y * qxAvg + perpDir.y * qyAvg
        const mag = Math.hypot(vx, vy)
        const dir = mag > 0 ? { x: vx / mag, y: vy / mag } : { x: 0, y: -1 }
        const screenDir = { x: dir.x, y: -dir.y }
        const arrowDist = (28 + stackIndex * 20) * k
        const arrowCount = 5
        for (let i = 0; i < arrowCount; i += 1) {
          const t = arrowCount === 1 ? 0.5 : i / (arrowCount - 1)
          const px = nodeI.x + (nodeJ.x - nodeI.x) * t
          const py = nodeI.y + (nodeJ.y - nodeI.y) * t
          const tip = modelToWorld(px, py)
          const tail = { x: tip.x - screenDir.x * arrowDist, y: tip.y - screenDir.y * arrowDist }
          const segDistance = pointToSegmentDistance(pointer.x, pointer.y, tail.x, tail.y, tip.x, tip.y)
          if (segDistance < distance) distance = segDistance
        }

        const midTip = modelToWorld((nodeI.x + nodeJ.x) / 2, (nodeI.y + nodeJ.y) / 2)
        const labelDist = arrowDist + 14 * k
        const text = `qx:${l.qxI}/${l.qxJ} qy:${l.qyI}/${l.qyJ} [${l.loadCase}]`
        const labelRect = estimateLabelRect(
          text,
          midTip.x - screenDir.x * labelDist,
          midTip.y - screenDir.y * labelDist - 14 * k,
          fontSize
        )
        distance = Math.min(
          distance,
          pointToRectDistance(pointer.x, pointer.y, labelRect.x, labelRect.y, labelRect.width, labelRect.height)
        )
      }

      consider(distance, { type: 'elementLoad', key: l.key, label: `Carga no elemento ${l.element} [${l.loadCase}]` })
    })

    return candidates.sort((a, b) => a.distance - b.distance)
  }

  function updateSelected(field, value) {
    if (!selected) return
    if (selected.type === 'node') {
      setNodes((prev) => prev.map((n) => (n.key === selected.key ? { ...n, [field]: value } : n)))
    } else if (selected.type === 'element') {
      setElements((prev) => prev.map((el) => (el.key === selected.key ? { ...el, [field]: value } : el)))
    } else if (selected.type === 'support') {
      setSupports((prev) => prev.map((s) => (s.key === selected.key ? { ...s, [field]: value } : s)))
    } else if (selected.type === 'nodalLoad') {
      setNodalLoads((prev) => prev.map((l) => (l.key === selected.key ? { ...l, [field]: value } : l)))
      if (field !== 'loadCase') setLastNodalLoadValues((prev) => ({ ...prev, [field]: value }))
    } else if (selected.type === 'elementLoad') {
      setElementLoads((prev) => prev.map((l) => (l.key === selected.key ? { ...l, [field]: value } : l)))
      if (field !== 'loadCase') setLastElementLoadValues((prev) => ({ ...prev, [field]: value }))
    }
  }

  // Overrides an element's section for a single combination (e.g. a cracked/reduced section for an
  // ELS check) — every other combination and the element itself keep the default section. Edited
  // from the element's own properties panel rather than the library modal, since it's a per-element
  // setting the user reaches for right after picking that element.
  function updateElementSectionOverride(elementId, combinationKey, sectionId) {
    setCombinations((prev) =>
      prev.map((c) => {
        if (c.key !== combinationKey) return c
        const elementOverrides = { ...(c.elementOverrides || {}) }
        if (sectionId === '') {
          delete elementOverrides[elementId]
        } else {
          elementOverrides[elementId] = sectionId
        }
        return { ...c, elementOverrides }
      })
    )
  }

  function deleteSelected() {
    if (!selected) return
    if (selected.type === 'node') {
      const result = cascadeRemoveNode(selected.key, {
        nodes,
        elements,
        supports,
        nodalLoads,
        elementLoads,
        combinations,
      })
      setNodes(result.nodes)
      setElements(result.elements)
      setSupports(result.supports)
      setNodalLoads(result.nodalLoads)
      setElementLoads(result.elementLoads)
      setCombinations(result.combinations)
    } else if (selected.type === 'element') {
      const result = cascadeRemoveElement(selected.key, { elements, elementLoads, combinations })
      setElements(result.elements)
      setElementLoads(result.elementLoads)
      setCombinations(result.combinations)
    } else if (selected.type === 'support') {
      setSupports((prev) => prev.filter((s) => s.key !== selected.key))
    } else if (selected.type === 'nodalLoad') {
      setNodalLoads((prev) => prev.filter((l) => l.key !== selected.key))
    } else if (selected.type === 'elementLoad') {
      setElementLoads((prev) => prev.filter((l) => l.key !== selected.key))
    }
    setSelected(null)
    setNearbyCandidates([])
  }

  // Deletes every node/element in a marquee selection in one pass. Removing a node also drops its
  // attached elements, supports and loads (matching cascadeRemoveNode); explicitly selected
  // elements drop their own element loads too.
  function deleteMultiSelection() {
    if (multiSelection.length === 0) return
    const nodeKeys = new Set(multiSelection.filter((s) => s.type === 'node').map((s) => s.key))
    const elementKeys = new Set(multiSelection.filter((s) => s.type === 'element').map((s) => s.key))
    const supportKeys = new Set(multiSelection.filter((s) => s.type === 'support').map((s) => s.key))
    const removedNodeIds = new Set(nodes.filter((n) => nodeKeys.has(n.key)).map((n) => n.id))
    const removedElements = elements.filter(
      (e) => elementKeys.has(e.key) || removedNodeIds.has(e.nodeI) || removedNodeIds.has(e.nodeJ)
    )
    const removedElementIds = new Set(removedElements.map((e) => e.id))
    const removedElementKeys = new Set(removedElements.map((e) => e.key))

    setNodes((prev) => prev.filter((n) => !nodeKeys.has(n.key)))
    setElements((prev) => prev.filter((e) => !removedElementKeys.has(e.key)))
    setSupports((prev) => prev.filter((s) => !removedNodeIds.has(s.node) && !supportKeys.has(s.key)))
    setNodalLoads((prev) => prev.filter((l) => !removedNodeIds.has(l.node)))
    setElementLoads((prev) => prev.filter((l) => !removedElementIds.has(l.element)))
    setCombinations((prev) =>
      prev.map((c) => {
        if (!c.elementOverrides) return c
        const elementOverrides = Object.fromEntries(
          Object.entries(c.elementOverrides).filter(([elementId]) => !removedElementIds.has(elementId))
        )
        return { ...c, elementOverrides }
      })
    )
    setMultiSelection([])
    setSelected(null)
    setNearbyCandidates([])
  }

  // Applies the nodal load typed in the multi-selection form (Fx/Fy/Mz) to every selected node in the
  // active load case: nodes that already have a load in that case are updated, the others get a new one.
  function applyNodalLoadToSelectedNodes() {
    if (!activeLoadCase) {
      toast.error('Crie um caso de carga antes de aplicar cargas')
      return
    }
    const selectedNodeIds = new Set(
      nodes.filter((n) => multiSelection.some((s) => s.type === 'node' && s.key === n.key)).map((n) => n.id)
    )
    if (selectedNodeIds.size === 0) return
    const hasLoad = new Set()
    const updated = nodalLoads.map((l) => {
      if (l.loadCase !== activeLoadCase || !selectedNodeIds.has(l.node)) return l
      hasLoad.add(l.node)
      return { ...l, ...lastNodalLoadValues }
    })
    const created = [...selectedNodeIds]
      .filter((id) => !hasLoad.has(id))
      .map((id) => ({ ...createNodalLoad(id, activeLoadCase), ...lastNodalLoadValues }))
    setNodalLoads([...updated, ...created])
    toast.success(`Carga aplicada a ${selectedNodeIds.size} nó${selectedNodeIds.size > 1 ? 's' : ''}`)
  }

  // Sets a release flag (releaseI/releaseJ) on every element of the marquee multi-selection at once.
  function setMultiRelease(field, value) {
    const keys = new Set(multiSelection.filter((s) => s.type === 'element').map((s) => s.key))
    if (keys.size === 0) return
    setElements((prev) => prev.map((el) => (keys.has(el.key) ? { ...el, [field]: value } : el)))
  }

  // Splits the selected element into N equal segments, creating the intermediate nodes (see
  // splitElement for how loads, releases, overrides and moving-load paths are carried over).
  function handleSplitSelected() {
    if (selected?.type !== 'element') return
    const parts = Math.floor(Number(splitParts))
    if (!(parts >= 2 && parts <= 50)) {
      toast.error('Informe um número de partes entre 2 e 50')
      return
    }
    const result = splitElement(selected.key, parts, {
      nodes,
      elements,
      elementLoads,
      combinations,
      movingLoadsPath,
    })
    if (!result) return
    setNodes(result.nodes)
    setElements(result.elements)
    setElementLoads(result.elementLoads)
    setCombinations(result.combinations)
    if (setMovingLoadsPath && result.movingLoadsPath) setMovingLoadsPath(result.movingLoadsPath)
    setSelected({ type: 'element', key: result.firstSegmentKey })
    setNearbyCandidates([])
    toast.success(`Elemento dividido em ${parts} partes`)
  }

  // Duplicating an element is a two-step "pick up and drop" gesture: startDuplicateElement captures
  // the selected element's geometry/properties into pendingDuplicate (a ghost that follows the
  // cursor), and the next canvas click drops it via placeDuplicate.
  function startDuplicateElement() {
    if (selected?.type !== 'element') return
    const el = elements.find((e) => e.key === selected.key)
    if (!el) return
    const nodeI = nodes.find((n) => n.id === el.nodeI)
    const nodeJ = nodes.find((n) => n.id === el.nodeJ)
    if (!nodeI || !nodeJ) return

    setPendingDuplicate({
      delta: { x: Number(nodeJ.x) - Number(nodeI.x), y: Number(nodeJ.y) - Number(nodeI.y) },
      sectionId: el.sectionId,
      releaseI: !!el.releaseI,
      releaseJ: !!el.releaseJ,
      // Loads travel with the copy: the element's own loads and the nodal loads on each end node.
      elementLoads: elementLoads.filter((l) => l.element === el.id),
      nodalLoadsI: nodalLoads.filter((l) => l.node === nodeI.id),
      nodalLoadsJ: nodalLoads.filter((l) => l.node === nodeJ.id),
      pos: { x: snap(Number(nodeI.x), snapSize), y: snap(Number(nodeI.y), snapSize) },
    })
  }

  // Drops the pending copy: node I at the (snapped) cursor anchor and node J preserving the original
  // I->J vector, so the copy keeps the same length/orientation, section, releases and loads. The
  // pending copy is kept alive so several copies can be dropped in a row (space bar ends it).
  function placeDuplicate(pending, anchorX, anchorY) {
    const nodeIds = nodes.map((n) => n.id)
    const idI = uniqueSequentialId('N', nodeIds)
    const idJ = uniqueSequentialId('N', [...nodeIds, idI])
    const newNodeI = { ...createNode(nodes, anchorX, anchorY), id: idI }
    const newNodeJ = {
      ...createNode(nodes, anchorX + pending.delta.x, anchorY + pending.delta.y),
      id: idJ,
    }
    const newElement = {
      ...createElement(elements, idI, idJ, pending.sectionId),
      id: uniqueSequentialId(
        'E',
        elements.map((e) => e.id)
      ),
      releaseI: pending.releaseI,
      releaseJ: pending.releaseJ,
    }

    const copiedElementLoads = pending.elementLoads.map((l) => ({ ...l, key: nextRowKey(), element: newElement.id }))
    const copiedNodalLoads = [
      ...pending.nodalLoadsI.map((l) => ({ ...l, key: nextRowKey(), node: idI })),
      ...pending.nodalLoadsJ.map((l) => ({ ...l, key: nextRowKey(), node: idJ })),
    ]

    setNodes((prev) => [...prev, newNodeI, newNodeJ])
    setElements((prev) => [...prev, newElement])
    if (copiedElementLoads.length > 0) setElementLoads((prev) => [...prev, ...copiedElementLoads])
    if (copiedNodalLoads.length > 0) setNodalLoads((prev) => [...prev, ...copiedNodalLoads])
    setSelected({ type: 'element', key: newElement.key })
    setNearbyCandidates([])
  }

  // The node/element keys to clone for a duplication, derived from the current selection (a marquee
  // multi-selection, or a single element/node). Returns null when nothing duplicable is selected.
  function collectDuplicationTargets() {
    if (multiSelection.length > 0) {
      return {
        nodeKeys: new Set(multiSelection.filter((s) => s.type === 'node').map((s) => s.key)),
        elementKeys: new Set(multiSelection.filter((s) => s.type === 'element').map((s) => s.key)),
      }
    }
    if (selected?.type === 'element') return { nodeKeys: new Set(), elementKeys: new Set([selected.key]) }
    if (selected?.type === 'node') return { nodeKeys: new Set([selected.key]), elementKeys: new Set() }
    return null
  }

  // Duplicates the current selection offset by (dx, dy) meters. Every selected element drags its two
  // endpoint nodes along (cloned once and shared, so a connected frame stays connected in the copy);
  // element loads and nodal loads ride with their new elements/nodes. Supports are intentionally not
  // copied (a duplicated span rarely wants the same restraints). The copies become the new selection
  // so pressing duplicate again steps another delta further.
  function duplicateByDelta(dx, dy) {
    const targets = collectDuplicationTargets()
    if (!targets) return
    const selectedElements = elements.filter((e) => targets.elementKeys.has(e.key))

    const nodeIdsToClone = new Set()
    nodes.forEach((n) => {
      if (targets.nodeKeys.has(n.key)) nodeIdsToClone.add(n.id)
    })
    selectedElements.forEach((e) => {
      nodeIdsToClone.add(e.nodeI)
      nodeIdsToClone.add(e.nodeJ)
    })

    // Weld tolerance (m): a duplicated node landing on top of an existing one reuses that node
    // instead of stacking a coincident duplicate, so the copy stays connected to the structure.
    const WELD_EPS = 1e-6
    const matchPool = nodes.map((n) => ({ id: n.id, x: Number(n.x), y: Number(n.y) }))
    const reservedNodeIds = nodes.map((n) => n.id)
    const nodeIdMap = {}
    const newNodes = []
    const newNodeIds = new Set()
    nodes.forEach((n) => {
      if (!nodeIdsToClone.has(n.id)) return
      const nx = Number(n.x) + dx
      const ny = Number(n.y) + dy
      const existing = matchPool.find((m) => Math.abs(m.x - nx) < WELD_EPS && Math.abs(m.y - ny) < WELD_EPS)
      if (existing) {
        nodeIdMap[n.id] = existing.id
        return
      }
      const newId = uniqueSequentialId('N', reservedNodeIds)
      reservedNodeIds.push(newId)
      nodeIdMap[n.id] = newId
      newNodeIds.add(newId)
      newNodes.push({ ...n, key: nextRowKey(), id: newId, x: nx, y: ny })
      matchPool.push({ id: newId, x: nx, y: ny })
    })

    const reservedElementIds = elements.map((e) => e.id)
    const elementIdMap = {}
    const newElements = []
    selectedElements.forEach((e) => {
      const newId = uniqueSequentialId('E', reservedElementIds)
      reservedElementIds.push(newId)
      elementIdMap[e.id] = newId
      newElements.push({ ...e, key: nextRowKey(), id: newId, nodeI: nodeIdMap[e.nodeI], nodeJ: nodeIdMap[e.nodeJ] })
    })

    if (newNodes.length === 0 && newElements.length === 0) return

    const newElementLoads = elementLoads
      .filter((l) => elementIdMap[l.element])
      .map((l) => ({ ...l, key: nextRowKey(), element: elementIdMap[l.element] }))
    // Only newly-created nodes get their loads copied; a node welded onto an existing one keeps the
    // existing node's own loads (adding the source's would double up on that joint).
    const newNodalLoads = nodalLoads
      .filter((l) => newNodeIds.has(nodeIdMap[l.node]))
      .map((l) => ({ ...l, key: nextRowKey(), node: nodeIdMap[l.node] }))

    setNodes((prev) => [...prev, ...newNodes])
    if (newElements.length) setElements((prev) => [...prev, ...newElements])
    if (newElementLoads.length) setElementLoads((prev) => [...prev, ...newElementLoads])
    if (newNodalLoads.length) setNodalLoads((prev) => [...prev, ...newNodalLoads])

    setNearbyCandidates([])
    if (multiSelection.length > 0) {
      // A marquee copy stays a marquee selection (collapsing to a single selection if only one
      // item was actually copied), so repeating steps the whole group again.
      applySelection([
        ...newElements.map((e) => ({ type: 'element', key: e.key })),
        ...newNodes.map((n) => ({ type: 'node', key: n.key })),
      ])
    } else {
      // A single-item copy re-selects just that item, keeping the familiar single-item workflow.
      setMultiSelection([])
      setSelected(
        newElements.length > 0 ? { type: 'element', key: newElements[0].key } : { type: 'node', key: newNodes[0].key }
      )
    }
  }

  // Canvas keyboard shortcuts: Delete/Backspace removes the selected item, Space switches to the
  // select tool. Kept in a ref so the single window-level listener always sees the latest state
  // without being re-attached every render. Returns true when it acted (to preventDefault).
  const handleCanvasKeyRef = useRef()
  handleCanvasKeyRef.current = (e) => {
    if (disabled || showLibrary || showViewOptions) return false
    if (e.key === 'Escape') {
      if (pendingElementNode) {
        setPendingElementNode(null)
        return true
      }
      if (pendingDuplicate) {
        setPendingDuplicate(null)
        return true
      }
      if (multiSelection.length > 0) {
        setMultiSelection([])
        return true
      }
    }
    if (e.key === 'Delete' || e.key === 'Backspace') {
      if (multiSelection.length > 0) {
        deleteMultiSelection()
        return true
      }
      if (!selected) return false
      deleteSelected()
      return true
    }
    if (e.code === 'Space' || e.key === ' ') {
      // Space bar finishes the repeat-duplicate gesture; otherwise it switches to the select tool.
      if (pendingDuplicate) {
        setPendingDuplicate(null)
        return true
      }
      if (pendingElementNode) {
        setPendingElementNode(null)
        return true
      }
      if (mode === 'select') return false
      selectMode('select')
      return true
    }
    if (e.key === 'f' || e.key === 'F') {
      toggleFullscreen()
      return true
    }
    return false
  }

  useEffect(() => {
    function handleKeyDown(e) {
      // Don't hijack keys while the user is editing a form field (space/backspace/delete must
      // stay usable there).
      const target = e.target
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      ) {
        return
      }
      if (handleCanvasKeyRef.current(e)) e.preventDefault()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  const gridLines = useMemo(() => {
    const lines = []
    const extent = GRID_EXTENT * BASE_SCALE
    const stepX = Number(gridSpacingX) > 0 ? Number(gridSpacingX) : DEFAULT_GRID_SPACING
    const stepY = Number(gridSpacingY) > 0 ? Number(gridSpacingY) : DEFAULT_GRID_SPACING
    const countX = Math.min(Math.floor(GRID_EXTENT / stepX), MAX_GRID_LINES)
    const countY = Math.min(Math.floor(GRID_EXTENT / stepY), MAX_GRID_LINES)
    // Vertical lines are spaced by stepX along X; horizontal lines by stepY along Y. The x=0 / y=0
    // lines stay highlighted as the axes.
    for (let n = -countX; n <= countX; n += 1) {
      const x = n * stepX * BASE_SCALE
      lines.push({ key: `v${n}`, points: [x, -extent, x, extent], axis: n === 0 })
    }
    for (let n = -countY; n <= countY; n += 1) {
      const y = n * stepY * BASE_SCALE
      lines.push({ key: `h${n}`, points: [-extent, y, extent, y], axis: n === 0 })
    }
    return lines
  }, [gridSpacingX, gridSpacingY])

  const nodesById = useMemo(() => {
    const map = {}
    nodes.forEach((n) => {
      map[n.id] = n
    })
    return map
  }, [nodes])

  const elementsById = useMemo(() => {
    const map = {}
    elements.forEach((el) => {
      map[el.id] = el
    })
    return map
  }, [elements])

  // All solved trains (the backend may return one object or an array). Each carries its own
  // sections/states/envelope and, for train-combinations, a `combinations[]` list.
  const movingResponses = useMemo(() => normalizeMovingResponses(movingLoadsResult), [movingLoadsResult])

  const movingResponse = useMemo(() => {
    if (movingResponses.length === 0) return null
    return movingResponses.find((r) => (r.train?.id ?? r.id) === movingResponseId) || movingResponses[0]
  }, [movingResponses, movingResponseId])

  // Combinations of the active train that involve it (the backend already filters these into the
  // train's movingResponse — combinations without a train term don't appear here).
  const movingCombinations = useMemo(() => movingResponse?.combinations || [], [movingResponse])

  // The object actually feeding the diagram builders: the pure train, or one of its combinations
  // with the train's section mesh attached (combinations reuse `sections[]`, see HANDOFF.md).
  const activeMovingSource = useMemo(() => {
    if (!movingResponse) return null
    if (movingSource === 'train') return movingResponse
    const comb = movingCombinations.find((c) => c.id === movingSource)
    return comb ? { ...comb, sections: movingResponse.sections } : movingResponse
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [movingResponse, movingSource])

  useEffect(() => {
    if (movingResponses.length === 0 && analysisView !== 'static') setAnalysisView('static')
  }, [movingResponses, analysisView])

  // Keep the active train / source valid as results change.
  useEffect(() => {
    if (movingResponses.length === 0) return
    const stillValid = movingResponses.some((r) => (r.train?.id ?? r.id) === movingResponseId)
    if (!stillValid) setMovingResponseId(movingResponses[0].train?.id ?? movingResponses[0].id ?? null)
  }, [movingResponses, movingResponseId])

  useEffect(() => {
    if (movingSource !== 'train' && !movingCombinations.some((c) => c.id === movingSource)) {
      setMovingSource('train')
    }
  }, [movingCombinations, movingSource])

  // The N/V/M toggle cluster is shared between the two analyses: which underlying mode it reads
  // from/writes to depends on which one is currently selected, so the toolbar doesn't repeat a
  // second "Normal (N)/Cortante (V)/Momento (M)" cluster for moving loads.
  const activeAnalysisIsMoving = Boolean(movingResponse) && analysisView === 'moving'
  const currentDiagramMode = activeAnalysisIsMoving ? movingDiagramMode : diagramMode
  function setCurrentDiagramMode(nextMode) {
    if (activeAnalysisIsMoving) setMovingDiagramMode(nextMode)
    else setDiagramMode(nextMode)
  }

  const movingLoadsPathLayout = useMemo(
    () => buildPathLayout(movingLoadsPath, elementsById, nodesById),
    [movingLoadsPath, elementsById, nodesById]
  )

  useEffect(() => {
    if (!activeMovingSource) {
      if (movingStateSelection !== 'envelope') setMovingStateSelection('envelope')
      return
    }
    if (movingStateSelection === 'envelope') return
    if (!(activeMovingSource.states || []).some((s) => s.referencePosition === Number(movingStateSelection))) {
      setMovingStateSelection('envelope')
    }
  }, [activeMovingSource, movingStateSelection])

  // Not gated by the displayed diagram mode: the N/V/M data is the same regardless of which mode is
  // on screen, and the "Esforço em uma posição" query needs it even when the canvas shows no ribbon
  // ("Sem diagrama"). The render blocks below still check movingDiagramMode before drawing. Reads
  // from activeMovingSource so it covers the pure train and its combinations uniformly.
  // Full-beam max/min envelope for the active train/combination, independent of which state
  // (envelope or a single reference position) is currently on screen — "Dimensionar As" always
  // designs for the worst case across the whole envelope, not just whatever is displayed.
  const movingEnvelopeDiagramInfo = useMemo(() => {
    if (!activeMovingSource) return null
    const computed = buildEnvelopeDiagram(activeMovingSource)
    const native = buildNativeEnvelopeDiagram(activeMovingSource.envelope, movingLoadsPathLayout)
    return mergeEnvelopeDiagrams(computed, native)
  }, [activeMovingSource, movingLoadsPathLayout])

  const movingDiagramInfo = useMemo(() => {
    if (!activeMovingSource) return null
    if (movingStateSelection === 'envelope') return movingEnvelopeDiagramInfo
    return buildStateDiagram(activeMovingSource, Number(movingStateSelection))
  }, [activeMovingSource, movingStateSelection, movingEnvelopeDiagramInfo])

  const movingLoadsAxleMarkers = useMemo(() => {
    if (!movingResponse || movingStateSelection === 'envelope' || movingLoadsPathLayout.length === 0) return []
    const state = movingResponse.states.find((s) => s.referencePosition === Number(movingStateSelection))
    if (!state) return []
    return (state.axles || [])
      .map((axle) => {
        const point = pathPositionToModelPoint(movingLoadsPathLayout, axle.position)
        return point && { ...axle, ...point }
      })
      .filter(Boolean)
  }, [movingResponse, movingStateSelection, movingLoadsPathLayout])

  const movingLoadsAxleMaxAbs = useMemo(
    () => movingLoadsAxleMarkers.reduce((max, axle) => Math.max(max, Math.abs(Number(axle.value)) || 0), 0),
    [movingLoadsAxleMarkers]
  )

  const MOVING_BAND_ARROW_COUNT = 5

  const movingLoadsDistributedBands = useMemo(() => {
    if (!movingResponse || movingStateSelection === 'envelope' || movingLoadsPathLayout.length === 0) return []
    const state = movingResponse.states.find((s) => s.referencePosition === Number(movingStateSelection))
    if (!state) return []
    return (state.distributedLoads || [])
      .map((band) => {
        const points = []
        for (let i = 0; i < MOVING_BAND_ARROW_COUNT; i += 1) {
          const t = MOVING_BAND_ARROW_COUNT === 1 ? 0.5 : i / (MOVING_BAND_ARROW_COUNT - 1)
          const position = band.start + (band.end - band.start) * t
          const point = pathPositionToModelPoint(movingLoadsPathLayout, position)
          if (point) points.push(point)
        }
        return points.length > 0 ? { ...band, points } : null
      })
      .filter(Boolean)
  }, [movingResponse, movingStateSelection, movingLoadsPathLayout])

  const movingLoadsBandMaxAbs = useMemo(
    () => movingLoadsDistributedBands.reduce((max, band) => Math.max(max, Math.abs(Number(band.value)) || 0), 0),
    [movingLoadsDistributedBands]
  )

  const elementColor = theme.mode === 'dark' ? '#c2b280' : '#8a7d3a'
  const nodeColor = theme.colors.textPrimary
  const gridColor = theme.mode === 'dark' ? '#1f2a3d' : '#e2e8f0'
  const axisColor = theme.mode === 'dark' ? '#334155' : '#cbd5e1'
  const selectionColor = theme.colors.primary
  const loadColor = theme.mode === 'dark' ? '#f87171' : '#dc2626'
  const reactionColor = theme.mode === 'dark' ? '#34d399' : '#16a34a'
  const displacementColor = theme.mode === 'dark' ? '#e879f9' : '#c026d3'
  // Loads can be hidden to declutter, but stay visible while a load tool is active so newly placed
  // loads don't seem to vanish.
  const loadsVisible = showLoads || mode === 'nodalLoad' || mode === 'elementLoad'
  const diagramColors = {
    N: theme.mode === 'dark' ? '#38bdf8' : '#0891b2',
    V: theme.mode === 'dark' ? '#fbbf24' : '#d97706',
    M: theme.mode === 'dark' ? '#c084fc' : '#7c3aed',
  }
  const movingLoadsOverlayColor = theme.mode === 'dark' ? '#5eead4' : '#0d9488'
  // AutoCAD-style marquee colors: blue window (left-to-right, fully enclosed) vs. green crossing
  // (right-to-left, anything touched).
  const marqueeWindowColor = theme.mode === 'dark' ? '#60a5fa' : '#2563eb'
  const marqueeCrossingColor = theme.mode === 'dark' ? '#4ade80' : '#16a34a'

  // O(1) membership lookups for highlighting the marquee's multi-selection.
  const selectedElementKeys = useMemo(
    () => new Set(multiSelection.filter((s) => s.type === 'element').map((s) => s.key)),
    [multiSelection]
  )
  const selectedNodeKeys = useMemo(
    () => new Set(multiSelection.filter((s) => s.type === 'node').map((s) => s.key)),
    [multiSelection]
  )
  const selectedSupportKeys = useMemo(
    () => new Set(multiSelection.filter((s) => s.type === 'support').map((s) => s.key)),
    [multiSelection]
  )

  // The Stage scales its whole content by `scale` on zoom, so any marker/glyph drawn in world
  // units grows with the geometry — fine for a 5 m beam, but a node dot or support icon then dwarfs
  // a 0.15 m stair step at any zoom. Multiplying a marker's size/offset by `k = 1/scale` cancels the
  // Stage scaling, keeping it a constant number of screen pixels while the geometry still zooms. Only
  // decorations get `k`; true positions (node anchors, the grid) are left to scale spatially.
  const k = 1 / scale

  // Numeric view-scale multipliers (see `viewScales` state). Applied on top of `k` so the neutral
  // 1x keeps today's sizes; the deformed multiplier is baked into `deformedShape.scale` above.
  const nodeScaleF = positiveScale(viewScales.node)
  const supportScaleF = positiveScale(viewScales.support)
  const diagramScaleF = positiveScale(viewScales.diagram)
  const textScaleF = positiveScale(viewScales.text)

  // Δx/Δy inputs plus a button that duplicates the current selection by that offset. Shared by the
  // single-item and marquee multi-selection property blocks.
  function renderDuplicateControls() {
    return (
      <div style={{ marginTop: '10px' }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          <PropertyRow style={{ flex: 1, marginBottom: 0 }}>
            Δx (m)
            <input
              type="number"
              step={0.01}
              value={duplicateDelta.x}
              onChange={(e) => setDuplicateDelta((prev) => ({ ...prev, x: e.target.value }))}
              disabled={disabled}
            />
          </PropertyRow>
          <PropertyRow style={{ flex: 1, marginBottom: 0 }}>
            Δy (m)
            <input
              type="number"
              step={0.01}
              value={duplicateDelta.y}
              onChange={(e) => setDuplicateDelta((prev) => ({ ...prev, y: e.target.value }))}
              disabled={disabled}
            />
          </PropertyRow>
        </div>
        <ToolButton
          type="button"
          onClick={() => duplicateByDelta(Number(duplicateDelta.x) || 0, Number(duplicateDelta.y) || 0)}
          disabled={disabled}
          style={{ width: '100%', justifyContent: 'center', marginTop: '10px' }}
        >
          <FiCopy /> Duplicar por Δx, Δy
        </ToolButton>
      </div>
    )
  }

  return (
    <CanvasWrapper ref={wrapperRef} $isFullscreen={isFullscreen}>
      <Toolbar>
        <ToolButton type="button" $active={mode === 'select'} onClick={() => selectMode('select')} disabled={disabled}>
          <FiMousePointer /> Selecionar
        </ToolButton>
        <ToolButton type="button" $active={mode === 'node'} onClick={() => selectMode('node')} disabled={disabled}>
          <FiCircle /> Nó
        </ToolButton>
        <ToolButton
          type="button"
          $active={mode === 'support'}
          onClick={() => selectMode('support')}
          disabled={disabled}
        >
          <FiAnchor /> Apoio
        </ToolButton>
        <ToolButton
          type="button"
          $active={mode === 'element'}
          onClick={() => selectMode('element')}
          disabled={disabled}
        >
          <FiMinus /> Elemento
        </ToolButton>
        <ToolButton
          type="button"
          $active={mode === 'nodalLoad'}
          onClick={() => selectMode('nodalLoad')}
          disabled={disabled}
        >
          <FiArrowDown /> Carga Nodal
        </ToolButton>
        <ToolButton
          type="button"
          $active={mode === 'elementLoad'}
          onClick={() => selectMode('elementLoad')}
          disabled={disabled}
        >
          <FiTrendingDown /> Carga no Elemento
        </ToolButton>
        {loadCases.length > 1 && (
          <ToolbarSelect
            value={activeLoadCase}
            onChange={(e) => setActiveLoadCase(e.target.value)}
            disabled={disabled}
            title="Novas cargas nodais/de elemento são criadas neste caso"
          >
            {loadCases.map((c) => (
              <option key={c.key} value={c.id}>
                Editando: {c.name || c.id}
              </option>
            ))}
          </ToolbarSelect>
        )}
        <ToolbarSeparator />
        <ToolButton type="button" onClick={() => setShowLibrary(true)} disabled={disabled}>
          <FiLayers /> Biblioteca
        </ToolButton>
        <ToolButton type="button" onClick={() => setShowMovingLoads(true)} disabled={disabled}>
          <FiTrendingUp /> Cargas Móveis
        </ToolButton>
        <ToolbarSeparator />
        <ToolButton type="button" onClick={() => zoomBy(1.2)} disabled={disabled} title="Aproximar">
          <FiZoomIn />
        </ToolButton>
        <ToolButton type="button" onClick={() => zoomBy(1 / 1.2)} disabled={disabled} title="Afastar">
          <FiZoomOut />
        </ToolButton>
        <ToolButton type="button" onClick={resetView} disabled={disabled} title="Restaurar vista">
          <FiMaximize2 />
        </ToolButton>
        <ToolButton type="button" onClick={toggleFullscreen} title={isFullscreen ? 'Sair da tela cheia' : 'Tela cheia'}>
          {isFullscreen ? <FiMinimize /> : <FiMaximize />}
        </ToolButton>
        <ToolbarSeparator />
        <ToolButton
          type="button"
          $active={showLoads}
          onClick={() => setShowLoads((prev) => !prev)}
          title="Mostrar cargas"
        >
          {showLoads ? <FiEye /> : <FiEyeOff />} Cargas
        </ToolButton>
        <ToolButton
          type="button"
          $active={showNodeLabels}
          onClick={() => setShowNodeLabels((prev) => !prev)}
          title="Mostrar rótulos dos nós"
        >
          <FiTag /> Nós
        </ToolButton>
        <ToolButton
          type="button"
          $active={showElementLabels}
          onClick={() => setShowElementLabels((prev) => !prev)}
          title="Mostrar rótulos dos elementos"
        >
          <FiTag /> Elementos
        </ToolButton>
        <ToolbarSeparator />
        <ToolButton type="button" onClick={() => setShowViewOptions(true)} title="Snap e escalas de exibição">
          <FiSliders /> Opções de visualização
        </ToolButton>
        {/* In fullscreen, drop the results/action controls to a second toolbar row. */}
        {isFullscreen && <ToolbarBreak />}
        {resultViews.length > 0 && (
          <>
            {!isFullscreen && <ToolbarSeparator />}
            {movingResponse && (
              <>
                <ToolButton type="button" $active={analysisView === 'static'} onClick={() => setAnalysisView('static')}>
                  Estática
                </ToolButton>
                <ToolButton type="button" $active={analysisView === 'moving'} onClick={() => setAnalysisView('moving')}>
                  Carga Móvel
                </ToolButton>
              </>
            )}
            {!activeAnalysisIsMoving && resultViews.length > 1 && (
              <ToolbarSelect
                value={selectedView ? `${selectedView.type}:${selectedView.id}` : ''}
                onChange={(e) => {
                  const [type, id] = e.target.value.split(':')
                  setSelectedView({ type, id })
                }}
              >
                {resultViews.map((v) => (
                  <option key={`${v.type}-${v.id}`} value={`${v.type}:${v.id}`}>
                    {v.type === 'combination' ? '∑ ' : ''}
                    {v.label}
                  </option>
                ))}
              </ToolbarSelect>
            )}
            {activeAnalysisIsMoving && movingCombinations.length > 0 && (
              <ToolbarSelect
                value={movingSource}
                onChange={(e) => setMovingSource(e.target.value)}
                title="Trem puro ou uma combinação que envolve este trem"
              >
                <option value="train">Trem puro</option>
                {movingCombinations.map((c) => (
                  <option key={c.id} value={c.id}>
                    Combinação: {c.name || c.id}
                  </option>
                ))}
              </ToolbarSelect>
            )}
            <ToolButton type="button" $active={showReactions} onClick={() => setShowReactions((prev) => !prev)}>
              <FiActivity /> Reações
            </ToolButton>
            <ToolButton type="button" $active={showDisplacements} onClick={() => setShowDisplacements((prev) => !prev)}>
              <FiMove /> Deslocamentos
            </ToolButton>
            <ToolButton
              type="button"
              $active={currentDiagramMode === 'none'}
              onClick={() => setCurrentDiagramMode('none')}
            >
              Sem diagrama
            </ToolButton>
            <ToolButton type="button" $active={currentDiagramMode === 'N'} onClick={() => setCurrentDiagramMode('N')}>
              Normal (N)
            </ToolButton>
            <ToolButton type="button" $active={currentDiagramMode === 'V'} onClick={() => setCurrentDiagramMode('V')}>
              Cortante (V)
            </ToolButton>
            <ToolButton type="button" $active={currentDiagramMode === 'M'} onClick={() => setCurrentDiagramMode('M')}>
              Momento (M)
            </ToolButton>
            {activeAnalysisIsMoving && movingResponses.length > 1 && (
              <ToolbarSelect
                value={movingResponseId ?? ''}
                onChange={(e) => setMovingResponseId(e.target.value)}
                title="Trem-tipo em exibição"
              >
                {movingResponses.map((r) => {
                  const id = r.train?.id ?? r.id
                  return (
                    <option key={id} value={id}>
                      Trem: {r.train?.name || r.train?.id || r.name || id}
                    </option>
                  )
                })}
              </ToolbarSelect>
            )}
            {activeAnalysisIsMoving && (
              <ToolbarSelect value={movingStateSelection} onChange={(e) => setMovingStateSelection(e.target.value)}>
                <option value="envelope">Cargas móveis: envoltória (máx/mín)</option>
                {(activeMovingSource?.states || []).map((s) => (
                  <option key={s.referencePosition} value={s.referencePosition}>
                    Cargas móveis: trem em {roundToDecimals(s.referencePosition, 2)}
                  </option>
                ))}
              </ToolbarSelect>
            )}
          </>
        )}
        <ToolbarSeparator />
        <ToolButton type="button" onClick={onSave} disabled={disabled}>
          <FiSave /> Salvar
        </ToolButton>
        <ToolButton type="button" onClick={() => fileInputRef.current?.click()} disabled={disabled}>
          <FiUpload /> Carregar
        </ToolButton>
        <ToolButton type="button" $active onClick={onCalculate} disabled={!isFormValid || disabled || !canCalculate}>
          {disabled ? (
            <>
              <Spinner size="14px" /> Calculando...
            </>
          ) : !canCalculate ? (
            'Limite atingido'
          ) : (
            <>
              <FiPlay /> Calcular
            </>
          )}
        </ToolButton>
        <input type="file" ref={fileInputRef} onChange={onFileUpload} style={{ display: 'none' }} />
      </Toolbar>
      <HintBar>
        {pendingDuplicate
          ? 'Clique para soltar cópias do elemento (com cargas) — barra de espaço finaliza, Esc cancela.'
          : HINTS[mode]}
      </HintBar>
      {formErrors?.length > 0 && (
        <FormErrorsBox>
          <strong>Corrija antes de calcular:</strong>
          <ul>
            {formErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </FormErrorsBox>
      )}
      <CanvasBody $isFullscreen={isFullscreen}>
        <StageHost
          ref={hostRef}
          $cursor={pendingDuplicate ? 'copy' : mode === 'select' ? 'default' : 'crosshair'}
          style={{ height: isFullscreen ? '100%' : `${STAGE_HEIGHT}px` }}
        >
          <Stage
            ref={stageRef}
            width={stageWidth}
            height={stageHeight}
            scaleX={scale}
            scaleY={scale}
            x={stagePos.x}
            y={stagePos.y}
            draggable={!disabled && !pendingDuplicate && mode !== 'select'}
            onDragEnd={(e) => {
              if (e.target === e.target.getStage()) {
                setStagePos({ x: e.target.x(), y: e.target.y() })
              }
            }}
            onWheel={handleWheel}
            onMouseDown={handleStageMouseDown}
            onMouseMove={handleStageMouseMove}
            onMouseUp={handleStageMouseUp}
            onMouseLeave={handleStageMouseUp}
            onClick={handleStageClick}
            onTap={handleStageClick}
          >
            <Layer listening={false}>
              {gridLines.map((line) => (
                <Line
                  key={line.key}
                  points={line.points}
                  stroke={line.axis ? axisColor : gridColor}
                  strokeWidth={(line.axis ? 1.5 : 1) * k}
                />
              ))}
            </Layer>

            <Layer>
              {elements.map((el) => {
                const nodeI = nodesById[el.nodeI]
                const nodeJ = nodesById[el.nodeJ]
                if (!nodeI || !nodeJ) return null
                const pI = modelToWorld(nodeI.x, nodeI.y)
                const pJ = modelToWorld(nodeJ.x, nodeJ.y)
                const isSelected =
                  (selected?.type === 'element' && selected.key === el.key) || selectedElementKeys.has(el.key)
                return (
                  <Group key={el.key}>
                    <Line
                      points={[pI.x, pI.y, pJ.x, pJ.y]}
                      stroke={isSelected ? selectionColor : elementColor}
                      strokeWidth={(isSelected ? 4 : 3) * k}
                      hitStrokeWidth={16 * k}
                      onClick={(e) => handleElementClick(el, e)}
                      onTap={(e) => handleElementClick(el, e)}
                    />
                    {[
                      { released: el.releaseI, from: pI, to: pJ },
                      { released: el.releaseJ, from: pJ, to: pI },
                    ].map(({ released, from, to }, endIndex) => {
                      if (!released) return null
                      // Hinge symbol: a hollow circle sitting on the bar, inset from the node so it
                      // doesn't hide the node marker or the support glyph.
                      const dist = Math.hypot(to.x - from.x, to.y - from.y)
                      if (dist === 0) return null
                      const inset = Math.min(11 * k, dist / 2)
                      return (
                        <Circle
                          key={endIndex}
                          x={from.x + ((to.x - from.x) / dist) * inset}
                          y={from.y + ((to.y - from.y) / dist) * inset}
                          radius={4.5 * k}
                          stroke={isSelected ? selectionColor : elementColor}
                          strokeWidth={2 * k}
                          fill={theme.colors.background}
                          listening={false}
                        />
                      )
                    })}
                    {showElementLabels && (
                      <Text
                        x={(pI.x + pJ.x) / 2 + 6 * k}
                        y={(pI.y + pJ.y) / 2 + 6 * k}
                        text={el.id}
                        fontSize={13 * k * textScaleF}
                        fill={theme.colors.textSecondary}
                        listening={false}
                      />
                    )}
                  </Group>
                )
              })}

              {!activeAnalysisIsMoving &&
                diagramMode !== 'none' &&
                diagramInfo &&
                elements.map((el) => {
                  const nodeI = nodesById[el.nodeI]
                  const nodeJ = nodesById[el.nodeJ]
                  const diagram = diagramInfo.diagramsByElementId[el.id]
                  if (!nodeI || !nodeJ || !diagram) return null

                  const { nx, ny, length } = elementVectors(nodeI, nodeJ)
                  const values = diagram[diagramMode]
                  const maxAbs = diagramInfo.maxAbs[diagramMode]
                  // Moment diagrams are conventionally drawn on the tension side (positive/sagging
                  // moment plotted downward, toward where the bottom reinforcement would go), which
                  // is the opposite screen direction from how N and V are plotted, hence the flip.
                  const amplitudeSign = diagramMode === 'M' ? -1 : 1
                  const amplitude = maxAbs > 0 ? ((40 * diagramScaleF) / maxAbs) * amplitudeSign : 0
                  const color = diagramColors[diagramMode]

                  const basePoints = []
                  const curveNodes = []
                  diagram.xs.forEach((x, i) => {
                    const t = x / length
                    const px = nodeI.x + (nodeJ.x - nodeI.x) * t
                    const py = nodeI.y + (nodeJ.y - nodeI.y) * t
                    const base = modelToWorld(px, py)
                    basePoints.push(base.x, base.y)
                    const offset = values[i] * amplitude
                    curveNodes.push({ x: base.x + nx * offset, y: base.y - ny * offset })
                  })

                  const curvePoints = curveNodes.flatMap((p) => [p.x, p.y])
                  const reversedCurvePoints = curveNodes
                    .slice()
                    .reverse()
                    .flatMap((p) => [p.x, p.y])
                  const ribbonPoints = [...basePoints, ...reversedCurvePoints]
                  const startLabelPos = curveNodes[0]
                  const endLabelPos = curveNodes[curveNodes.length - 1]

                  // Mark the internal extremum the backend found (only when it's strictly between the
                  // nodes — an extremum at a node is already shown by the end labels). We take the
                  // backend's location (xi) but read the value off the frontend's own curve so the
                  // label keeps the displayed diagram's sign convention.
                  const forceKey = diagramMode === 'N' ? 'axial' : diagramMode === 'V' ? 'shear' : 'moment'
                  const extreme = selectedResult?.elements?.find((r) => r.id === el.id)?.internalExtrema?.forces?.[
                    forceKey
                  ]
                  let extremeMarker = null
                  if (extreme && Number.isFinite(extreme.xi) && extreme.xi > 0.02 && extreme.xi < 0.98) {
                    const value = interpolateAt(diagram.xs, values, extreme.xi * length)
                    const px = nodeI.x + (nodeJ.x - nodeI.x) * extreme.xi
                    const py = nodeI.y + (nodeJ.y - nodeI.y) * extreme.xi
                    const base = modelToWorld(px, py)
                    extremeMarker = { x: base.x + nx * value * amplitude, y: base.y - ny * value * amplitude, value }
                  }

                  // The user-picked query position (click on the ribbon, or typed into the
                  // properties panel) is stored per element, independent of which diagram mode is
                  // showing — so it reappears at the same x when the user switches N/V/M.
                  let queryMarker = null
                  if (queryPoint && queryPoint.elementId === el.id) {
                    const qx = clamp(queryPoint.x, 0, length)
                    const value = interpolateAt(diagram.xs, values, qx)
                    const t = qx / length
                    const px = nodeI.x + (nodeJ.x - nodeI.x) * t
                    const py = nodeI.y + (nodeJ.y - nodeI.y) * t
                    const base = modelToWorld(px, py)
                    queryMarker = { x: base.x + nx * value * amplitude, y: base.y - ny * value * amplitude, value }
                  }

                  return (
                    <Group key={`diagram-${el.key}`}>
                      <Line
                        points={ribbonPoints}
                        closed
                        fill={`${color}33`}
                        stroke={color}
                        strokeWidth={1.5 * k}
                        onClick={(e) => handleDiagramQueryClick(el, nodeI, nodeJ, e)}
                        onTap={(e) => handleDiagramQueryClick(el, nodeI, nodeJ, e)}
                      />
                      <Line points={curvePoints} stroke={color} strokeWidth={2 * k} listening={false} />
                      <Text
                        x={startLabelPos.x + 4 * k}
                        y={startLabelPos.y - 4 * k}
                        text={`${roundToDecimals(values[0], 2)}`}
                        fontSize={11 * k * textScaleF}
                        fill={color}
                        listening={false}
                      />
                      <Text
                        x={endLabelPos.x + 4 * k}
                        y={endLabelPos.y - 4 * k}
                        text={`${roundToDecimals(values[values.length - 1], 2)}`}
                        fontSize={11 * k * textScaleF}
                        fill={color}
                        listening={false}
                      />
                      {extremeMarker && (
                        <>
                          <Circle
                            x={extremeMarker.x}
                            y={extremeMarker.y}
                            radius={4 * k}
                            fill={color}
                            listening={false}
                          />
                          <Text
                            x={extremeMarker.x + 5 * k}
                            y={extremeMarker.y - 15 * k}
                            text={`máx ${roundToDecimals(extremeMarker.value, 2)}`}
                            fontSize={11 * k * textScaleF}
                            fontStyle="bold"
                            fill={color}
                            listening={false}
                          />
                        </>
                      )}
                      {queryMarker && (
                        <>
                          <Circle
                            x={queryMarker.x}
                            y={queryMarker.y}
                            radius={5 * k}
                            stroke={color}
                            strokeWidth={2 * k}
                            fill={theme.colors.background}
                            listening={false}
                          />
                          <Text
                            x={queryMarker.x + 5 * k}
                            y={queryMarker.y + 6 * k}
                            text={`x=${roundToDecimals(queryPoint.x, 2)}: ${roundToDecimals(queryMarker.value, 2)}`}
                            fontSize={11 * k * textScaleF}
                            fontStyle="bold"
                            fill={color}
                            listening={false}
                          />
                        </>
                      )}
                    </Group>
                  )
                })}

              {activeAnalysisIsMoving &&
                movingDiagramMode !== 'none' &&
                movingDiagramInfo &&
                elements.map((el) => {
                  const nodeI = nodesById[el.nodeI]
                  const nodeJ = nodesById[el.nodeJ]
                  const diagram = movingDiagramInfo.diagramsByElementId[el.id]
                  if (!nodeI || !nodeJ || !diagram) return null

                  const { nx, ny, length } = elementVectors(nodeI, nodeJ)
                  const maxAbs = movingDiagramInfo.maxAbs[movingDiagramMode]
                  const amplitudeSign = movingDiagramMode === 'M' ? -1 : 1
                  const amplitude = maxAbs > 0 ? ((40 * diagramScaleF) / maxAbs) * amplitudeSign : 0
                  const color = movingLoadsOverlayColor

                  function buildRibbon(values, xs) {
                    const basePoints = []
                    const curveNodes = []
                    xs.forEach((x, i) => {
                      const t = x / length
                      const px = nodeI.x + (nodeJ.x - nodeI.x) * t
                      const py = nodeI.y + (nodeJ.y - nodeI.y) * t
                      const base = modelToWorld(px, py)
                      basePoints.push(base.x, base.y)
                      const offset = values[i] * amplitude
                      curveNodes.push({ x: base.x + nx * offset, y: base.y - ny * offset })
                    })
                    const curvePoints = curveNodes.flatMap((p) => [p.x, p.y])
                    const reversedCurvePoints = curveNodes
                      .slice()
                      .reverse()
                      .flatMap((p) => [p.x, p.y])
                    return {
                      ribbonPoints: [...basePoints, ...reversedCurvePoints],
                      curvePoints,
                      curveNodes,
                      values,
                      xs,
                    }
                  }

                  // Marks the peak only when the curve's true overall extreme (over every sample,
                  // including the boundaries) actually falls in the interior — e.g. a moment
                  // diagram peaking at midspan. For a monotonic curve (like a shear envelope that
                  // just decreases from +R at one support to -R at the other), the real extreme is
                  // at a boundary, already covered by the start/end labels; picking "the biggest
                  // interior-only value" in that case would just mark some arbitrary near-boundary
                  // sample, producing a label that doesn't match the true max and floats oddly
                  // close to (but not on) the start/end point.
                  function findExtremeIndex(values, xs) {
                    let globalIndex = 0
                    let globalAbs = Math.abs(values[0])
                    values.forEach((v, i) => {
                      if (Math.abs(v) > globalAbs) {
                        globalAbs = Math.abs(v)
                        globalIndex = i
                      }
                    })
                    const xi = length > 0 ? xs[globalIndex] / length : 0
                    return xi > 0.02 && xi < 0.98 ? globalIndex : null
                  }

                  function renderCurveLabels(curve, prefix, opacity) {
                    const lastIndex = curve.values.length - 1
                    const extremeIndex = findExtremeIndex(curve.values, curve.xs)
                    return (
                      <>
                        <Text
                          x={curve.curveNodes[0].x + 4 * k}
                          y={curve.curveNodes[0].y - 4 * k}
                          text={`${roundToDecimals(curve.values[0], 2)}`}
                          fontSize={11 * k * textScaleF}
                          fill={color}
                          opacity={opacity}
                        />
                        <Text
                          x={curve.curveNodes[lastIndex].x + 4 * k}
                          y={curve.curveNodes[lastIndex].y - 4 * k}
                          text={`${roundToDecimals(curve.values[lastIndex], 2)}`}
                          fontSize={11 * k * textScaleF}
                          fill={color}
                          opacity={opacity}
                        />
                        {extremeIndex !== null && (
                          <>
                            <Circle
                              x={curve.curveNodes[extremeIndex].x}
                              y={curve.curveNodes[extremeIndex].y}
                              radius={4 * k}
                              fill={color}
                              opacity={opacity}
                            />
                            <Text
                              x={curve.curveNodes[extremeIndex].x + 5 * k}
                              y={curve.curveNodes[extremeIndex].y - 15 * k}
                              text={`${prefix}${roundToDecimals(curve.values[extremeIndex], 2)}`}
                              fontSize={11 * k * textScaleF}
                              fontStyle="bold"
                              fill={color}
                              opacity={opacity}
                            />
                          </>
                        )}
                      </>
                    )
                  }

                  const isEnvelope = movingStateSelection === 'envelope'
                  const xs = isEnvelope ? diagram.xsByKey[movingDiagramMode] : diagram.xs
                  const maxCurve = buildRibbon(
                    isEnvelope ? diagram.max[movingDiagramMode] : diagram[movingDiagramMode],
                    xs
                  )
                  const minCurve = isEnvelope ? buildRibbon(diagram.min[movingDiagramMode], xs) : null

                  // In envelope mode the filled region must be the band between the max and min
                  // curves, not each curve filled down to the zero axis — the two bounds can share a
                  // sign (e.g. a support's shear envelope that never crosses zero), in which case
                  // zero-anchored fills would just overlap and hide the true lower bound instead of
                  // showing the real min-to-max range (see HANDOFF.md "envoltória de cortante").
                  const bandRibbonPoints =
                    isEnvelope && minCurve
                      ? [
                          ...maxCurve.curveNodes.flatMap((p) => [p.x, p.y]),
                          ...minCurve.curveNodes
                            .slice()
                            .reverse()
                            .flatMap((p) => [p.x, p.y]),
                        ]
                      : null

                  // Marker for the "Esforço em uma posição" query: the value on the displayed (max)
                  // curve at the queried x — the panel lists the full max/mín numbers alongside.
                  let queryMarker = null
                  if (queryPoint && queryPoint.elementId === el.id && xs) {
                    const qx = clamp(queryPoint.x, 0, length)
                    const value = interpolateAt(xs, maxCurve.values, qx)
                    const t = length > 0 ? qx / length : 0
                    const px = nodeI.x + (nodeJ.x - nodeI.x) * t
                    const py = nodeI.y + (nodeJ.y - nodeI.y) * t
                    const base = modelToWorld(px, py)
                    queryMarker = { x: base.x + nx * value * amplitude, y: base.y - ny * value * amplitude, value }
                  }

                  return (
                    <Group key={`moving-load-${el.key}`}>
                      {/* Filled ribbons are clickable to set the query position; the thin curves,
                          labels and marker below don't listen so clicks fall through to them. */}
                      <Line
                        points={bandRibbonPoints || maxCurve.ribbonPoints}
                        closed
                        fill={`${color}66`}
                        stroke={color}
                        strokeWidth={1.5 * k}
                        onClick={(e) => handleDiagramQueryClick(el, nodeI, nodeJ, e)}
                        onTap={(e) => handleDiagramQueryClick(el, nodeI, nodeJ, e)}
                      />
                      <Group listening={false}>
                        <Line points={maxCurve.curvePoints} stroke={color} strokeWidth={2 * k} />
                        {renderCurveLabels(maxCurve, isEnvelope ? 'máx ' : '', 1)}
                        {minCurve && (
                          <>
                            <Line points={minCurve.curvePoints} stroke={color} strokeWidth={2 * k} opacity={0.5} />
                            {renderCurveLabels(minCurve, 'mín ', 0.6)}
                          </>
                        )}
                        {queryMarker && (
                          <>
                            <Circle
                              x={queryMarker.x}
                              y={queryMarker.y}
                              radius={5 * k}
                              stroke={color}
                              strokeWidth={2 * k}
                              fill={theme.colors.background}
                            />
                            <Text
                              x={queryMarker.x + 5 * k}
                              y={queryMarker.y + 6 * k}
                              text={`x=${roundToDecimals(queryPoint.x, 2)}: ${roundToDecimals(queryMarker.value, 2)}`}
                              fontSize={11 * k * textScaleF}
                              fontStyle="bold"
                              fill={color}
                            />
                          </>
                        )}
                      </Group>
                    </Group>
                  )
                })}

              {activeAnalysisIsMoving &&
                movingLoadsAxleMarkers.map((axle, index) => {
                  const value = Number(axle.value) || 0
                  const tip = modelToWorld(axle.x, axle.y)
                  const dirY = value === 0 ? -1 : value / Math.abs(value)
                  const screenDirY = -dirY
                  const ratio = movingLoadsAxleMaxAbs > 0 ? Math.abs(value) / movingLoadsAxleMaxAbs : 1
                  const arrowLength = (25 + 30 * ratio) * k
                  const tail = { x: tip.x, y: tip.y - screenDirY * arrowLength }
                  return (
                    <Group key={`moving-axle-${index}`} listening={false}>
                      <Arrow
                        points={[tail.x, tail.y, tip.x, tip.y]}
                        stroke={movingLoadsOverlayColor}
                        fill={movingLoadsOverlayColor}
                        strokeWidth={3 * k}
                        pointerLength={9 * k}
                        pointerWidth={9 * k}
                      />
                      <Text
                        x={tail.x - 10 * k}
                        y={tail.y - 16 * k}
                        text={`${roundToDecimals(value, 2)}`}
                        fontSize={11 * k * textScaleF}
                        fill={movingLoadsOverlayColor}
                      />
                    </Group>
                  )
                })}

              {activeAnalysisIsMoving &&
                movingLoadsDistributedBands.map((band, bandIndex) => {
                  const value = Number(band.value) || 0
                  const dirY = value === 0 ? -1 : value / Math.abs(value)
                  const screenDirY = -dirY
                  const ratio = movingLoadsBandMaxAbs > 0 ? Math.abs(value) / movingLoadsBandMaxAbs : 1
                  const arrowLength = (16 + 20 * ratio) * k
                  const midPoint = band.points[Math.floor(band.points.length / 2)]
                  const midTip = modelToWorld(midPoint.x, midPoint.y)
                  return (
                    <Group key={`moving-band-${bandIndex}`} listening={false}>
                      {band.points.map((p, i) => {
                        const tip = modelToWorld(p.x, p.y)
                        const tail = { x: tip.x, y: tip.y - screenDirY * arrowLength }
                        return (
                          <Arrow
                            key={i}
                            points={[tail.x, tail.y, tip.x, tip.y]}
                            stroke={movingLoadsOverlayColor}
                            fill={movingLoadsOverlayColor}
                            strokeWidth={2 * k}
                            pointerLength={6 * k}
                            pointerWidth={6 * k}
                            opacity={0.7}
                          />
                        )
                      })}
                      <Text
                        x={midTip.x - screenDirY * (arrowLength + 14 * k)}
                        y={midTip.y - screenDirY * (arrowLength + 14 * k) - 14 * k}
                        text={`${roundToDecimals(value, 2)}`}
                        fontSize={11 * k * textScaleF}
                        fill={movingLoadsOverlayColor}
                      />
                    </Group>
                  )
                })}

              {showDisplacements &&
                hasResult &&
                deformedShape &&
                deformedShape.polylines.map((pl) => {
                  // Only the selected element shows the "máx" deflection label; every element still
                  // gets the dot marking where its peak deflection is.
                  const isSelected = selected?.type === 'element' && selected.key === pl.key
                  return (
                    <Group key={`deformed-${pl.id}`} listening={false}>
                      <Line points={pl.points} stroke={displacementColor} strokeWidth={2 * k} dash={[6 * k, 4 * k]} />
                      {pl.marker && (
                        <>
                          <Circle
                            x={pl.marker.x}
                            y={pl.marker.y}
                            radius={4 * k}
                            stroke={displacementColor}
                            strokeWidth={2 * k}
                          />
                          {isSelected && (
                            <Text
                              x={pl.marker.x + 6 * k}
                              y={pl.marker.y - 4 * k}
                              text={`máx ${formatDisplacement(pl.marker.value, displacementUnit)} ${displacementUnit}`}
                              fontSize={11 * k * textScaleF}
                              fill={displacementColor}
                            />
                          )}
                        </>
                      )}
                    </Group>
                  )
                })}

              {showDisplacements &&
                hasResult &&
                deformedShape &&
                nodes.map((n) => {
                  const d = displacementByNode[n.id]
                  if (!d) return null
                  const dw = modelToWorld(
                    n.x + deformedShape.scale * (Number(d.u) || 0),
                    n.y + deformedShape.scale * (Number(d.v) || 0)
                  )
                  // Only the selected node shows its u/v/θ label, so a dense structure doesn't drown
                  // in overlapping text; every displaced node still gets a dot.
                  const isSelected = selected?.type === 'node' && selected.key === n.key
                  return (
                    <Group key={`disp-${n.id}`} listening={false}>
                      <Circle x={dw.x} y={dw.y} radius={4 * k} fill={displacementColor} />
                      {isSelected && (
                        <Text
                          x={dw.x + 8 * k}
                          y={dw.y - 6 * k}
                          text={`u=${formatDisplacement(d.u, displacementUnit)}${displacementUnit} v=${formatDisplacement(
                            d.v,
                            displacementUnit
                          )}${displacementUnit} θ=${formatDisplacement(d.theta)}`}
                          fontSize={11 * k * textScaleF}
                          fill={displacementColor}
                        />
                      )}
                    </Group>
                  )
                })}

              {showReactions &&
                hasResult &&
                selectedResult.reactions.map((reaction, index) => {
                  const node = nodesById[reaction.node]
                  if (!node) return null
                  const fx = Number(reaction.Fx) || 0
                  const fy = Number(reaction.Fy) || 0
                  const mz = Number(reaction.Mz) || 0
                  const fxLabel = Number(fx.toFixed(2))
                  const fyLabel = Number(fy.toFixed(2))
                  const mzLabel = Number(mz.toFixed(2))
                  const mag = Math.hypot(fx, fy)
                  const tip = modelToWorld(node.x, node.y)

                  if (mag === 0) {
                    if (mz === 0) return null
                    return (
                      <Group key={`reaction-${index}`} listening={false}>
                        <Circle
                          x={tip.x}
                          y={tip.y + 40 * k}
                          radius={9 * k}
                          stroke={reactionColor}
                          strokeWidth={2 * k}
                        />
                        <Text
                          x={tip.x - 10 * k}
                          y={tip.y + 52 * k}
                          text={`Mz=${mzLabel}`}
                          fontSize={11 * k * textScaleF}
                          fill={reactionColor}
                        />
                      </Group>
                    )
                  }

                  const dir = { x: fx / mag, y: fy / mag }
                  const screenDir = { x: dir.x, y: -dir.y }
                  const tail = { x: tip.x - screenDir.x * 55 * k, y: tip.y - screenDir.y * 55 * k }

                  return (
                    <Group key={`reaction-${index}`} listening={false}>
                      <Arrow
                        points={[tail.x, tail.y, tip.x, tip.y]}
                        stroke={reactionColor}
                        fill={reactionColor}
                        strokeWidth={3 * k}
                        pointerLength={9 * k}
                        pointerWidth={9 * k}
                      />
                      <Text
                        x={tail.x - 10 * k}
                        y={tail.y + 8 * k}
                        text={`Fx=${fxLabel} Fy=${fyLabel}${mz ? ` Mz=${mzLabel}` : ''}`}
                        fontSize={11 * k * textScaleF}
                        fill={reactionColor}
                      />
                    </Group>
                  )
                })}

              {loadsVisible &&
                (() => {
                  // Multiple loads on the same element (typically one per load case) would
                  // otherwise draw their arrows/labels at the exact same spot and become
                  // unreadable — see the "duas cargas" issue. Stacking each subsequent load on an
                  // element further away from it keeps every one visible at once instead of only
                  // the last one drawn.
                  const stackCountByElement = {}
                  return elementLoads.map((load) => {
                    const el = elements.find((e) => e.id === load.element)
                    if (!el) return null
                    const nodeI = nodesById[el.nodeI]
                    const nodeJ = nodesById[el.nodeJ]
                    if (!nodeI || !nodeJ) return null
                    const stackIndex = stackCountByElement[load.element] || 0
                    stackCountByElement[load.element] = stackIndex + 1
                    const { ux, uy, nx, ny, length } = elementVectors(nodeI, nodeJ)
                    const axialDir = load.coordinateSystem === 'global' ? { x: 1, y: 0 } : { x: ux, y: uy }
                    const perpDir = load.coordinateSystem === 'global' ? { x: 0, y: 1 } : { x: nx, y: ny }
                    const isSelected = selected?.type === 'elementLoad' && selected.key === load.key
                    const color = isSelected ? selectionColor : loadColor

                    if (load.type === 'point') {
                      const t = clamp(Number(load.position) || 0, 0, length) / length
                      const baseX = nodeI.x + (nodeJ.x - nodeI.x) * t
                      const baseY = nodeI.y + (nodeJ.y - nodeI.y) * t
                      const vx = axialDir.x * (Number(load.Px) || 0) + perpDir.x * (Number(load.Py) || 0)
                      const vy = axialDir.y * (Number(load.Px) || 0) + perpDir.y * (Number(load.Py) || 0)
                      const mag = Math.hypot(vx, vy)
                      const dir = mag > 0 ? { x: vx / mag, y: vy / mag } : { x: 0, y: -1 }
                      const tip = modelToWorld(baseX, baseY)
                      const screenDir = { x: dir.x, y: -dir.y }
                      const tailDist = (45 + stackIndex * 22) * k
                      const tail = { x: tip.x - screenDir.x * tailDist, y: tip.y - screenDir.y * tailDist }
                      return (
                        <Group
                          key={load.key}
                          onClick={(e) => handleElementLoadClick(load, e)}
                          onTap={(e) => handleElementLoadClick(load, e)}
                        >
                          <Arrow
                            points={[tail.x, tail.y, tip.x, tip.y]}
                            stroke={color}
                            fill={color}
                            strokeWidth={2.5 * k}
                            pointerLength={8 * k}
                            pointerWidth={8 * k}
                            hitStrokeWidth={16 * k}
                          />
                          <Text
                            x={tail.x - 10 * k}
                            y={tail.y - 16 * k}
                            text={`Px=${load.Px} Py=${load.Py}${Number(load.Mz) ? ` Mz=${load.Mz}` : ''} [${load.loadCase}]`}
                            fontSize={11 * k * textScaleF}
                            fill={color}
                          />
                        </Group>
                      )
                    }

                    const qxAvg = ((Number(load.qxI) || 0) + (Number(load.qxJ) || 0)) / 2
                    const qyAvg = ((Number(load.qyI) || 0) + (Number(load.qyJ) || 0)) / 2
                    const vx = axialDir.x * qxAvg + perpDir.x * qyAvg
                    const vy = axialDir.y * qxAvg + perpDir.y * qyAvg
                    const mag = Math.hypot(vx, vy)
                    const dir = mag > 0 ? { x: vx / mag, y: vy / mag } : { x: 0, y: -1 }
                    const screenDir = { x: dir.x, y: -dir.y }
                    const arrowDist = (28 + stackIndex * 20) * k
                    const arrowCount = 5
                    const arrows = []
                    for (let i = 0; i < arrowCount; i += 1) {
                      const t = arrowCount === 1 ? 0.5 : i / (arrowCount - 1)
                      const px = nodeI.x + (nodeJ.x - nodeI.x) * t
                      const py = nodeI.y + (nodeJ.y - nodeI.y) * t
                      const tip = modelToWorld(px, py)
                      const tail = { x: tip.x - screenDir.x * arrowDist, y: tip.y - screenDir.y * arrowDist }
                      arrows.push(
                        <Arrow
                          key={i}
                          points={[tail.x, tail.y, tip.x, tip.y]}
                          stroke={color}
                          fill={color}
                          strokeWidth={2 * k}
                          pointerLength={6 * k}
                          pointerWidth={6 * k}
                          hitStrokeWidth={14 * k}
                        />
                      )
                    }
                    const midTip = modelToWorld((nodeI.x + nodeJ.x) / 2, (nodeI.y + nodeJ.y) / 2)
                    const labelDist = arrowDist + 14 * k
                    return (
                      <Group
                        key={load.key}
                        onClick={(e) => handleElementLoadClick(load, e)}
                        onTap={(e) => handleElementLoadClick(load, e)}
                      >
                        {arrows}
                        <Text
                          x={midTip.x - screenDir.x * labelDist}
                          y={midTip.y - screenDir.y * labelDist - 14 * k}
                          text={`qx:${load.qxI}/${load.qxJ} qy:${load.qyI}/${load.qyJ} [${load.loadCase}]`}
                          fontSize={11 * k * textScaleF}
                          fill={color}
                        />
                      </Group>
                    )
                  })
                })()}

              {supports.map((s) => {
                const node = nodesById[s.node]
                if (!node) return null
                const pos = modelToWorld(node.x, node.y)
                const glyph = supportGlyphType(s)
                const isSelected =
                  (selected?.type === 'support' && selected.key === s.key) || selectedSupportKeys.has(s.key)
                const color = isSelected ? selectionColor : nodeColor
                const onClick = (e) => handleSupportClick(s, e)

                if (glyph === 'custom') {
                  const labels = []
                  if (s.restrainU) labels.push('U')
                  if (s.restrainV) labels.push('V')
                  if (s.restrainTheta) labels.push('θ')
                  return (
                    <Group key={s.key} onClick={onClick} onTap={onClick}>
                      <Rect
                        x={pos.x - 7 * k * supportScaleF}
                        y={pos.y + 6 * k * supportScaleF}
                        width={14 * k * supportScaleF}
                        height={14 * k * supportScaleF}
                        fill={color}
                      />
                      <Text
                        x={pos.x - 12 * k * supportScaleF}
                        y={pos.y + 22 * k * supportScaleF}
                        text={labels.join(',') || '-'}
                        fontSize={11 * k * textScaleF}
                        fill={color}
                      />
                    </Group>
                  )
                }

                if (glyph === 'fixed') {
                  const away = findAwaySupportDirection(s.node, elements, nodesById)
                  const perp = { x: -away.y, y: away.x }
                  const awayScreen = { x: away.x, y: -away.y }
                  const perpScreen = { x: perp.x, y: -perp.y }
                  const wallHalf = 15 * k * supportScaleF
                  const wallStart = { x: pos.x - perpScreen.x * wallHalf, y: pos.y - perpScreen.y * wallHalf }
                  const wallEnd = { x: pos.x + perpScreen.x * wallHalf, y: pos.y + perpScreen.y * wallHalf }
                  const hatchOffsets = [0.15, 0.4, 0.65, 0.9]

                  return (
                    <Group key={s.key} onClick={onClick} onTap={onClick}>
                      <Line
                        points={[wallStart.x, wallStart.y, wallEnd.x, wallEnd.y]}
                        stroke={color}
                        strokeWidth={2.5 * k}
                      />
                      {hatchOffsets.map((t, i) => {
                        const bx = wallStart.x + (wallEnd.x - wallStart.x) * t
                        const by = wallStart.y + (wallEnd.y - wallStart.y) * t
                        return (
                          <Line
                            key={i}
                            points={[
                              bx,
                              by,
                              bx + awayScreen.x * 11 * k * supportScaleF,
                              by + awayScreen.y * 11 * k * supportScaleF,
                            ]}
                            stroke={color}
                            strokeWidth={1.5 * k}
                          />
                        )
                      })}
                    </Group>
                  )
                }

                const trianglePoints = [
                  pos.x,
                  pos.y + 2 * k * supportScaleF,
                  pos.x - 11 * k * supportScaleF,
                  pos.y + 22 * k * supportScaleF,
                  pos.x + 11 * k * supportScaleF,
                  pos.y + 22 * k * supportScaleF,
                ]

                return (
                  <Group key={s.key} onClick={onClick} onTap={onClick}>
                    <Line points={trianglePoints} closed stroke={color} strokeWidth={2 * k} fill="transparent" />
                    {glyph === 'pinned' && (
                      <>
                        <Line
                          points={[
                            pos.x - 13 * k * supportScaleF,
                            pos.y + 22 * k * supportScaleF,
                            pos.x + 13 * k * supportScaleF,
                            pos.y + 22 * k * supportScaleF,
                          ]}
                          stroke={color}
                          strokeWidth={2 * k}
                        />
                        {[0, 1, 2, 3].map((i) => (
                          <Line
                            key={i}
                            points={[
                              pos.x + (-11 + i * 8) * k * supportScaleF,
                              pos.y + 22 * k * supportScaleF,
                              pos.x + (-15 + i * 8) * k * supportScaleF,
                              pos.y + 29 * k * supportScaleF,
                            ]}
                            stroke={color}
                            strokeWidth={1.5 * k}
                          />
                        ))}
                      </>
                    )}
                    {glyph === 'roller' && (
                      <>
                        <Line
                          points={[
                            pos.x - 11 * k * supportScaleF,
                            pos.y + 27 * k * supportScaleF,
                            pos.x + 11 * k * supportScaleF,
                            pos.y + 27 * k * supportScaleF,
                          ]}
                          stroke={color}
                          strokeWidth={2 * k}
                        />
                        <Line
                          points={[
                            pos.x - 11 * k * supportScaleF,
                            pos.y + 31 * k * supportScaleF,
                            pos.x + 11 * k * supportScaleF,
                            pos.y + 31 * k * supportScaleF,
                          ]}
                          stroke={color}
                          strokeWidth={2 * k}
                        />
                      </>
                    )}
                  </Group>
                )
              })}

              {loadsVisible &&
                nodalLoads.map((load) => {
                  const node = nodesById[load.node]
                  if (!node) return null
                  const isSelected = selected?.type === 'nodalLoad' && selected.key === load.key
                  const color = isSelected ? selectionColor : loadColor
                  const tip = modelToWorld(node.x, node.y)
                  const fx = Number(load.Fx) || 0
                  const fy = Number(load.Fy) || 0
                  const mag = Math.hypot(fx, fy)
                  const onClick = (e) => {
                    if (mode === 'select') return
                    e.cancelBubble = true
                    setSelected({ type: 'nodalLoad', key: load.key })
                  }

                  if (mag === 0) {
                    return (
                      <Group key={load.key} onClick={onClick} onTap={onClick}>
                        <Circle x={tip.x} y={tip.y - 18 * k} radius={9 * k} stroke={color} strokeWidth={2 * k} />
                        <Text
                          x={tip.x - 10 * k}
                          y={tip.y - 40 * k}
                          text={`Mz=${load.Mz} [${load.loadCase}]`}
                          fontSize={11 * k * textScaleF}
                          fill={color}
                        />
                      </Group>
                    )
                  }

                  const dir = { x: fx / mag, y: fy / mag }
                  const screenDir = { x: dir.x, y: -dir.y }
                  const tail = { x: tip.x - screenDir.x * 45 * k, y: tip.y - screenDir.y * 45 * k }

                  return (
                    <Group key={load.key} onClick={onClick} onTap={onClick}>
                      <Arrow
                        points={[tail.x, tail.y, tip.x, tip.y]}
                        stroke={color}
                        fill={color}
                        strokeWidth={3 * k}
                        pointerLength={9 * k}
                        pointerWidth={9 * k}
                        hitStrokeWidth={18 * k}
                      />
                      <Text
                        x={tail.x - 10 * k}
                        y={tail.y - 16 * k}
                        text={`Fx=${load.Fx} Fy=${load.Fy}${Number(load.Mz) ? ` Mz=${load.Mz}` : ''} [${load.loadCase}]`}
                        fontSize={11 * k * textScaleF}
                        fill={color}
                      />
                    </Group>
                  )
                })}

              {nodes.map((node) => {
                const pos = modelToWorld(node.x, node.y)
                const isSelected =
                  (selected?.type === 'node' && selected.key === node.key) || selectedNodeKeys.has(node.key)
                const isPending = pendingElementNode === node.key
                return (
                  <Group key={node.key}>
                    <Circle
                      x={pos.x}
                      y={pos.y}
                      radius={(isSelected || isPending ? 7 : 5) * k * nodeScaleF}
                      fill={isPending ? '#f59e0b' : isSelected ? selectionColor : nodeColor}
                      draggable={mode === 'select' && !disabled && !pendingDuplicate}
                      onDragEnd={(e) => handleNodeDragEnd(node, e)}
                      onClick={(e) => handleNodeClick(node, e)}
                      onTap={(e) => handleNodeClick(node, e)}
                      hitStrokeWidth={16 * k}
                    />
                    {showNodeLabels && (
                      <Text
                        x={pos.x + 8 * k}
                        y={pos.y - 18 * k}
                        text={node.id}
                        fontSize={13 * k * textScaleF}
                        fontStyle="bold"
                        fill={nodeColor}
                        listening={false}
                      />
                    )}
                  </Group>
                )
              })}

              {pendingDuplicate &&
                (() => {
                  const a = modelToWorld(pendingDuplicate.pos.x, pendingDuplicate.pos.y)
                  const b = modelToWorld(
                    pendingDuplicate.pos.x + pendingDuplicate.delta.x,
                    pendingDuplicate.pos.y + pendingDuplicate.delta.y
                  )
                  return (
                    <Group listening={false} opacity={0.6}>
                      <Line
                        points={[a.x, a.y, b.x, b.y]}
                        stroke={selectionColor}
                        strokeWidth={3 * k}
                        dash={[8 * k, 6 * k]}
                      />
                      <Circle x={a.x} y={a.y} radius={5 * k} fill={selectionColor} />
                      <Circle x={b.x} y={b.y} radius={5 * k} fill={selectionColor} />
                    </Group>
                  )
                })()}

              {marquee &&
                (() => {
                  const color = marquee.crossing ? marqueeCrossingColor : marqueeWindowColor
                  return (
                    <Rect
                      x={Math.min(marquee.x0, marquee.x1)}
                      y={Math.min(marquee.y0, marquee.y1)}
                      width={Math.abs(marquee.x1 - marquee.x0)}
                      height={Math.abs(marquee.y1 - marquee.y0)}
                      fill={`${color}22`}
                      stroke={color}
                      strokeWidth={1 * k}
                      dash={marquee.crossing ? [6 * k, 4 * k] : undefined}
                      listening={false}
                    />
                  )
                })()}
            </Layer>
          </Stage>
        </StageHost>

        <PropertiesPanel>
          <h4>Propriedades</h4>
          {!selected && multiSelection.length === 0 && (
            <PropertiesEmptyState>Selecione um item no canvas para editar suas propriedades.</PropertiesEmptyState>
          )}

          {multiSelection.length > 0 &&
            (() => {
              const elementCount = multiSelection.filter((s) => s.type === 'element').length
              const nodeCount = multiSelection.filter((s) => s.type === 'node').length
              const supportCount = multiSelection.filter((s) => s.type === 'support').length
              const parts = []
              if (elementCount) parts.push(`${elementCount} elemento${elementCount > 1 ? 's' : ''}`)
              if (nodeCount) parts.push(`${nodeCount} nó${nodeCount > 1 ? 's' : ''}`)
              if (supportCount) parts.push(`${supportCount} apoio${supportCount > 1 ? 's' : ''}`)
              return (
                <div style={{ marginBottom: '14px' }}>
                  <p style={{ fontSize: '13px', margin: '0 0 10px' }}>
                    <strong>{multiSelection.length} itens selecionados</strong>
                    {parts.length > 0 && <span style={{ opacity: 0.7 }}> ({parts.join(', ')})</span>}
                  </p>
                  <NearbyList style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    <h5>Itens selecionados</h5>
                    {multiSelection.map((item) => (
                      <NearbyItemButton
                        key={`${item.type}-${item.key}`}
                        type="button"
                        onClick={() => {
                          setSelected(item)
                          setMultiSelection([])
                        }}
                      >
                        {describeMultiSelectionItem(item)}
                      </NearbyItemButton>
                    ))}
                  </NearbyList>
                  {nodeCount > 0 && (
                    <div style={{ marginBottom: '10px' }}>
                      <strong style={{ fontSize: '13px' }}>
                        Carga nodal nos nós selecionados{activeLoadCase ? ` [${activeLoadCase}]` : ''}
                      </strong>
                      {['Fx', 'Fy', 'Mz'].map((field) => (
                        <PropertyRow key={field}>
                          {field}
                          <input
                            type="number"
                            value={lastNodalLoadValues[field]}
                            onChange={(e) => setLastNodalLoadValues((prev) => ({ ...prev, [field]: e.target.value }))}
                            disabled={disabled}
                          />
                        </PropertyRow>
                      ))}
                      <ToolButton
                        type="button"
                        onClick={applyNodalLoadToSelectedNodes}
                        disabled={disabled || !activeLoadCase}
                        style={{ width: '100%', justifyContent: 'center', marginTop: '6px' }}
                      >
                        <FiArrowDown /> Aplicar a {nodeCount} nó{nodeCount > 1 ? 's' : ''}
                      </ToolButton>
                    </div>
                  )}
                  {elementCount > 0 &&
                    [
                      { field: 'releaseI', label: 'Rótula no nó inicial' },
                      { field: 'releaseJ', label: 'Rótula no nó final' },
                    ].map(({ field, label }) => {
                      const selectedElements = elements.filter((el) =>
                        multiSelection.some((s) => s.type === 'element' && s.key === el.key)
                      )
                      const releasedCount = selectedElements.filter((el) => el[field]).length
                      const allReleased = releasedCount === selectedElements.length
                      const mixed = releasedCount > 0 && !allReleased
                      return (
                        <PropertyCheckboxRow key={field}>
                          <input
                            type="checkbox"
                            checked={allReleased}
                            ref={(input) => {
                              if (input) input.indeterminate = mixed
                            }}
                            onChange={(e) => setMultiRelease(field, e.target.checked)}
                            disabled={disabled}
                          />
                          {label}
                          {elementCount > 1 && <span style={{ opacity: 0.7 }}> (em {elementCount} elementos)</span>}
                        </PropertyCheckboxRow>
                      )
                    })}
                  {renderDuplicateControls()}
                  <ToolButton
                    type="button"
                    onClick={deleteMultiSelection}
                    disabled={disabled}
                    style={{ marginTop: '10px' }}
                  >
                    <FiTrash2 /> Excluir selecionados
                  </ToolButton>
                </div>
              )
            })()}

          {nearbyCandidates.length > 1 && (
            <NearbyList>
              <h5>Itens próximos ao clique</h5>
              {nearbyCandidates.map((candidate) => (
                <NearbyItemButton
                  key={`${candidate.type}-${candidate.key}`}
                  type="button"
                  $active={selected?.type === candidate.type && selected.key === candidate.key}
                  onClick={() => setSelected({ type: candidate.type, key: candidate.key })}
                >
                  {candidate.label}
                </NearbyItemButton>
              ))}
            </NearbyList>
          )}

          {selected?.type === 'node' &&
            (() => {
              const node = nodes.find((n) => n.key === selected.key)
              if (!node) return null
              return (
                <>
                  <PropertyRow>
                    Id
                    <input value={node.id} onChange={(e) => updateSelected('id', e.target.value)} disabled={disabled} />
                  </PropertyRow>
                  <PropertyRow>
                    x
                    <input
                      type="number"
                      value={node.x}
                      onChange={(e) => updateSelected('x', e.target.value)}
                      disabled={disabled}
                    />
                  </PropertyRow>
                  <PropertyRow>
                    y
                    <input
                      type="number"
                      value={node.y}
                      onChange={(e) => updateSelected('y', e.target.value)}
                      disabled={disabled}
                    />
                  </PropertyRow>
                  {hasResult && displacementByNode[node.id] && (
                    <div style={{ marginTop: '12px', fontSize: '13px', lineHeight: 1.5 }}>
                      <strong style={{ color: displacementColor }}>Deslocamentos</strong>
                      <div>
                        u = {formatDisplacement(displacementByNode[node.id].u, displacementUnit)} {displacementUnit}
                      </div>
                      <div>
                        v = {formatDisplacement(displacementByNode[node.id].v, displacementUnit)} {displacementUnit}
                      </div>
                      <div>θ = {formatDisplacement(displacementByNode[node.id].theta)}</div>
                    </div>
                  )}
                </>
              )
            })()}

          {selected?.type === 'element' &&
            (() => {
              const el = elements.find((e) => e.key === selected.key)
              if (!el) return null
              return (
                <>
                  <PropertyRow>
                    Id
                    <input value={el.id} onChange={(e) => updateSelected('id', e.target.value)} disabled={disabled} />
                  </PropertyRow>
                  <PropertyRow>
                    Nó inicial
                    <select
                      value={el.nodeI}
                      onChange={(e) => updateSelected('nodeI', e.target.value)}
                      disabled={disabled}
                    >
                      <option value="">-</option>
                      {nodes.map((n) => (
                        <option key={n.key} value={n.id}>
                          {n.id}
                        </option>
                      ))}
                    </select>
                  </PropertyRow>
                  <PropertyRow>
                    Nó final
                    <select
                      value={el.nodeJ}
                      onChange={(e) => updateSelected('nodeJ', e.target.value)}
                      disabled={disabled}
                    >
                      <option value="">-</option>
                      {nodes.map((n) => (
                        <option key={n.key} value={n.id}>
                          {n.id}
                        </option>
                      ))}
                    </select>
                  </PropertyRow>
                  <PropertyRow>
                    Seção
                    <select
                      value={el.sectionId}
                      onChange={(e) => updateSelected('sectionId', e.target.value)}
                      disabled={disabled}
                    >
                      <option value="">-</option>
                      {sections.map((s) => (
                        <option key={s.key} value={s.id}>
                          {s.id} {s.name ? `- ${s.name}` : ''}
                        </option>
                      ))}
                    </select>
                  </PropertyRow>
                  {(() => {
                    const section = sections.find((s) => s.id === el.sectionId)
                    const material = materials.find((m) => m.id === section?.materialId)
                    if (!section) {
                      return (
                        <PropertiesEmptyState>
                          Nenhuma seção atribuída. Crie uma em &quot;Materiais / Seções&quot;.
                        </PropertiesEmptyState>
                      )
                    }
                    const { A, I } = computeSectionProperties(section)
                    return (
                      <PropertiesEmptyState>
                        E = {material?.E ?? '-'} · A = {A.toFixed(5)} · I = {I.toFixed(7)}
                        {!material && ' (material não definido)'}
                      </PropertiesEmptyState>
                    )
                  })()}
                  {(() => {
                    const section = sections.find((s) => s.id === el.sectionId)
                    const material = materials.find((m) => m.id === section?.materialId)
                    if (material?.materialType !== 'concrete') return null
                    const designAsPayload = activeAnalysisIsMoving
                      ? buildMovingDesignAsPayload(el, section, material, movingEnvelopeDiagramInfo)
                      : buildDesignAsPayload(el, section, material)
                    const equivalentInertiaPayload = buildEquivalentInertiaPayload(el, section, material)
                    return (
                      <>
                        <PropertyRow>
                          As (cm²)
                          <input
                            type="number"
                            value={el.as}
                            onChange={(e) => updateSelected('as', e.target.value)}
                            disabled={disabled}
                          />
                        </PropertyRow>
                        <PropertyRow>
                          As&apos; (cm²)
                          <input
                            type="number"
                            value={el.asPrime}
                            onChange={(e) => updateSelected('asPrime', e.target.value)}
                            disabled={disabled}
                          />
                        </PropertyRow>
                        {designAsPayload && (
                          <ToolButton
                            type="button"
                            onClick={() => handleDesignAsClick(el, designAsPayload)}
                            disabled={disabled}
                            title={
                              activeAnalysisIsMoving
                                ? "Dimensiona As/As' via /concreto/flexao a partir do momento máximo da envoltória de carga móvel do elemento (esforço normal nulo)"
                                : "Dimensiona As/As' via /concreto/flexao a partir do momento máximo do elemento (esforço normal nulo)"
                            }
                            style={{ marginTop: '4px', marginBottom: '8px' }}
                          >
                            <FiTool /> Dimensionar As
                          </ToolButton>
                        )}
                        {equivalentInertiaPayload && (
                          <ToolButton
                            type="button"
                            onClick={() => handleEquivalentInertiaClick(section, equivalentInertiaPayload)}
                            disabled={disabled}
                            title="Cria uma seção genérica com a inércia equivalente de Branson (mesma área da seção atual), via /concreto/inercia-equivalente"
                            style={{ marginTop: '4px', marginBottom: '8px' }}
                          >
                            <FiBox /> Calcular inércia equivalente
                          </ToolButton>
                        )}
                      </>
                    )
                  })()}
                  <PropertyCheckboxRow>
                    <input
                      type="checkbox"
                      checked={el.releaseI}
                      onChange={(e) => updateSelected('releaseI', e.target.checked)}
                      disabled={disabled}
                    />
                    Rótula no nó inicial
                  </PropertyCheckboxRow>
                  <PropertyCheckboxRow>
                    <input
                      type="checkbox"
                      checked={el.releaseJ}
                      onChange={(e) => updateSelected('releaseJ', e.target.checked)}
                      disabled={disabled}
                    />
                    Rótula no nó final
                  </PropertyCheckboxRow>
                  {combinations.length > 0 && (
                    <div style={{ marginTop: '12px' }}>
                      <strong style={{ fontSize: '13px' }}>Seção por combinação</strong>
                      {combinations.map((c) => (
                        <PropertyRow key={c.key}>
                          {c.id || 'Combinação'}
                          <select
                            value={c.elementOverrides?.[el.id] ?? ''}
                            onChange={(e) => updateElementSectionOverride(el.id, c.key, e.target.value)}
                            disabled={disabled}
                          >
                            <option value="">Padrão ({el.sectionId || '-'})</option>
                            {sections.map((s) => (
                              <option key={s.key} value={s.id}>
                                {s.id} {s.name ? `- ${s.name}` : ''}
                              </option>
                            ))}
                          </select>
                        </PropertyRow>
                      ))}
                    </div>
                  )}
                  {hasResult &&
                    (() => {
                      const t = selectedResult?.elements?.find((r) => r.id === el.id)?.internalExtrema?.displacement
                        ?.transverse
                      if (!t) return null
                      return (
                        <div style={{ marginTop: '12px', fontSize: '13px', lineHeight: 1.5 }}>
                          <strong style={{ color: displacementColor }}>Flecha máxima</strong>
                          <div>
                            v = {formatDisplacement(t.value, displacementUnit)} {displacementUnit} em x ={' '}
                            {roundToDecimals(t.x, 3)} (xi = {roundToDecimals(t.xi, 3)})
                          </div>
                        </div>
                      )
                    })()}
                  {(() => {
                    // Query source: the moving-load diagram when viewing moving results, else the
                    // static one. A moving envelope carries max/min per component (xsByKey/max/min);
                    // a static or single-state diagram carries one value series per component.
                    const entry = activeAnalysisIsMoving
                      ? movingDiagramInfo?.diagramsByElementId?.[el.id]
                      : hasResult
                        ? diagramInfo?.diagramsByElementId?.[el.id]
                        : null
                    if (!entry) return null
                    const nodeI = nodesById[el.nodeI]
                    const nodeJ = nodesById[el.nodeJ]
                    if (!nodeI || !nodeJ) return null
                    const length = Math.hypot(nodeJ.x - nodeI.x, nodeJ.y - nodeI.y) || entry.length || 0
                    const x = queryPoint?.elementId === el.id ? queryPoint.x : 0
                    const isEnvelope = Boolean(entry.max)
                    return (
                      <div style={{ marginTop: '12px', fontSize: '13px', lineHeight: 1.5 }}>
                        <strong>Esforço em uma posição{activeAnalysisIsMoving ? ' (carga móvel)' : ''}</strong>
                        <PropertyRow>
                          x (m)
                          <input
                            type="number"
                            min={0}
                            max={length}
                            step={0.01}
                            value={roundToDecimals(x, 3)}
                            onChange={(e) => {
                              const raw = Number(e.target.value)
                              const clamped = clamp(Number.isFinite(raw) ? raw : 0, 0, length)
                              setQueryPoint({ elementId: el.id, x: clamped })
                            }}
                            disabled={disabled}
                          />
                        </PropertyRow>
                        {['N', 'V', 'M'].map((key) => {
                          if (isEnvelope) {
                            const xs = entry.xsByKey?.[key]
                            if (!xs) return null
                            const maxV = entry.max?.[key] ? interpolateAt(xs, entry.max[key], x) : 0
                            const minV = entry.min?.[key] ? interpolateAt(xs, entry.min[key], x) : 0
                            return (
                              <div key={key} style={{ color: diagramColors[key] }}>
                                {key}: máx {roundToDecimals(maxV, 2)} · mín {roundToDecimals(minV, 2)}
                              </div>
                            )
                          }
                          if (!entry[key]) return null
                          return (
                            <div key={key} style={{ color: diagramColors[key] }}>
                              {key} = {roundToDecimals(interpolateAt(entry.xs, entry[key], x), 2)}
                            </div>
                          )
                        })}
                        <div style={{ fontSize: '11px', opacity: 0.7, marginTop: '4px' }}>
                          0 ≤ x ≤ {roundToDecimals(length, 3)} m · também é possível clicar no diagrama para escolher a
                          posição
                        </div>
                      </div>
                    )
                  })()}
                </>
              )
            })()}

          {selected?.type === 'support' &&
            (() => {
              const s = supports.find((sup) => sup.key === selected.key)
              if (!s) return null
              return (
                <>
                  <PropertyRow>Nó: {s.node}</PropertyRow>
                  <PropertyCheckboxRow>
                    <input
                      type="checkbox"
                      checked={s.restrainU}
                      onChange={(e) => updateSelected('restrainU', e.target.checked)}
                      disabled={disabled}
                    />
                    Restringe u
                  </PropertyCheckboxRow>
                  <PropertyCheckboxRow>
                    <input
                      type="checkbox"
                      checked={s.restrainV}
                      onChange={(e) => updateSelected('restrainV', e.target.checked)}
                      disabled={disabled}
                    />
                    Restringe v
                  </PropertyCheckboxRow>
                  <PropertyCheckboxRow>
                    <input
                      type="checkbox"
                      checked={s.restrainTheta}
                      onChange={(e) => updateSelected('restrainTheta', e.target.checked)}
                      disabled={disabled}
                    />
                    Restringe θ
                  </PropertyCheckboxRow>
                  <PropertyRow>
                    u imposto
                    <input
                      type="number"
                      placeholder="-"
                      value={s.prescribedU}
                      onChange={(e) => updateSelected('prescribedU', e.target.value)}
                      disabled={disabled}
                    />
                  </PropertyRow>
                  <PropertyRow>
                    v imposto
                    <input
                      type="number"
                      placeholder="-"
                      value={s.prescribedV}
                      onChange={(e) => updateSelected('prescribedV', e.target.value)}
                      disabled={disabled}
                    />
                  </PropertyRow>
                  <PropertyRow>
                    θ imposto
                    <input
                      type="number"
                      placeholder="-"
                      value={s.prescribedTheta}
                      onChange={(e) => updateSelected('prescribedTheta', e.target.value)}
                      disabled={disabled}
                    />
                  </PropertyRow>
                </>
              )
            })()}

          {selected?.type === 'nodalLoad' &&
            (() => {
              const l = nodalLoads.find((load) => load.key === selected.key)
              if (!l) return null
              return (
                <>
                  <PropertyRow>Nó: {l.node}</PropertyRow>
                  <PropertyRow>
                    Caso de carga
                    <select
                      value={l.loadCase}
                      onChange={(e) => updateSelected('loadCase', e.target.value)}
                      disabled={disabled}
                    >
                      <option value="">-</option>
                      {loadCases.map((c) => (
                        <option key={c.key} value={c.id}>
                          {c.name || c.id}
                        </option>
                      ))}
                    </select>
                  </PropertyRow>
                  <PropertyRow>
                    Fx
                    <input
                      type="number"
                      value={l.Fx}
                      onChange={(e) => updateSelected('Fx', e.target.value)}
                      disabled={disabled}
                    />
                  </PropertyRow>
                  <PropertyRow>
                    Fy
                    <input
                      type="number"
                      value={l.Fy}
                      onChange={(e) => updateSelected('Fy', e.target.value)}
                      disabled={disabled}
                    />
                  </PropertyRow>
                  <PropertyRow>
                    Mz
                    <input
                      type="number"
                      value={l.Mz}
                      onChange={(e) => updateSelected('Mz', e.target.value)}
                      disabled={disabled}
                    />
                  </PropertyRow>
                </>
              )
            })()}

          {selected?.type === 'elementLoad' &&
            (() => {
              const l = elementLoads.find((load) => load.key === selected.key)
              if (!l) return null
              return (
                <>
                  <PropertyRow>Elemento: {l.element}</PropertyRow>
                  <PropertyRow>
                    Caso de carga
                    <select
                      value={l.loadCase}
                      onChange={(e) => updateSelected('loadCase', e.target.value)}
                      disabled={disabled}
                    >
                      <option value="">-</option>
                      {loadCases.map((c) => (
                        <option key={c.key} value={c.id}>
                          {c.name || c.id}
                        </option>
                      ))}
                    </select>
                  </PropertyRow>
                  <PropertyRow>
                    Tipo
                    <select value={l.type} onChange={(e) => updateSelected('type', e.target.value)} disabled={disabled}>
                      <option value="distributed">Distribuída</option>
                      <option value="point">Pontual</option>
                    </select>
                  </PropertyRow>
                  <PropertyRow>
                    Sistema de coordenadas
                    <select
                      value={l.coordinateSystem}
                      onChange={(e) => updateSelected('coordinateSystem', e.target.value)}
                      disabled={disabled}
                    >
                      <option value="local">Local</option>
                      <option value="global">Global</option>
                    </select>
                  </PropertyRow>
                  {l.type === 'point' ? (
                    <>
                      <PropertyRow>
                        Posição
                        <input
                          type="number"
                          value={l.position}
                          onChange={(e) => updateSelected('position', e.target.value)}
                          disabled={disabled}
                        />
                      </PropertyRow>
                      <PropertyRow>
                        Px
                        <input
                          type="number"
                          value={l.Px}
                          onChange={(e) => updateSelected('Px', e.target.value)}
                          disabled={disabled}
                        />
                      </PropertyRow>
                      <PropertyRow>
                        Py
                        <input
                          type="number"
                          value={l.Py}
                          onChange={(e) => updateSelected('Py', e.target.value)}
                          disabled={disabled}
                        />
                      </PropertyRow>
                      <PropertyRow>
                        Mz
                        <input
                          type="number"
                          value={l.Mz}
                          onChange={(e) => updateSelected('Mz', e.target.value)}
                          disabled={disabled}
                        />
                      </PropertyRow>
                    </>
                  ) : (
                    <>
                      <PropertyRow>
                        qxI
                        <input
                          type="number"
                          value={l.qxI}
                          onChange={(e) => updateSelected('qxI', e.target.value)}
                          disabled={disabled}
                        />
                      </PropertyRow>
                      <PropertyRow>
                        qxJ
                        <input
                          type="number"
                          value={l.qxJ}
                          onChange={(e) => updateSelected('qxJ', e.target.value)}
                          disabled={disabled}
                        />
                      </PropertyRow>
                      <PropertyRow>
                        qyI
                        <input
                          type="number"
                          value={l.qyI}
                          onChange={(e) => updateSelected('qyI', e.target.value)}
                          disabled={disabled}
                        />
                      </PropertyRow>
                      <PropertyRow>
                        qyJ
                        <input
                          type="number"
                          value={l.qyJ}
                          onChange={(e) => updateSelected('qyJ', e.target.value)}
                          disabled={disabled}
                        />
                      </PropertyRow>
                    </>
                  )}
                </>
              )
            })()}

          {(selected?.type === 'element' || selected?.type === 'node') && renderDuplicateControls()}

          {selected?.type === 'element' && (
            <ToolButton
              type="button"
              onClick={startDuplicateElement}
              disabled={disabled || !!pendingDuplicate}
              style={{ width: '100%', justifyContent: 'center', marginTop: '10px' }}
            >
              <FiCopy /> {pendingDuplicate ? 'Soltando cópias… (espaço finaliza)' : 'Duplicar no cursor'}
            </ToolButton>
          )}

          {selected?.type === 'element' && (
            <PropertyRow style={{ marginTop: '10px' }}>
              Dividir em partes iguais
              <input
                type="number"
                min="2"
                max="50"
                step="1"
                value={splitParts}
                onChange={(e) => setSplitParts(e.target.value)}
                disabled={disabled}
              />
            </PropertyRow>
          )}
          {selected?.type === 'element' && (
            <ToolButton
              type="button"
              onClick={handleSplitSelected}
              disabled={disabled}
              style={{ width: '100%', justifyContent: 'center', marginTop: '10px' }}
            >
              <FiScissors /> Dividir elemento
            </ToolButton>
          )}

          {selected && (
            <ToolButton
              type="button"
              onClick={deleteSelected}
              disabled={disabled}
              style={{ width: '100%', justifyContent: 'center', marginTop: '10px' }}
            >
              <FiTrash2 /> Remover
            </ToolButton>
          )}
        </PropertiesPanel>
      </CanvasBody>
      {showLibrary && (
        <LibraryPanel
          materials={materials}
          sections={sections}
          loadCases={loadCases}
          combinations={combinations}
          elements={elements}
          trains={trains}
          nodalLoads={nodalLoads}
          elementLoads={elementLoads}
          setMaterials={setMaterials}
          setSections={setSections}
          setLoadCases={setLoadCases}
          setCombinations={setCombinations}
          setElements={setElements}
          setNodalLoads={setNodalLoads}
          setElementLoads={setElementLoads}
          onClose={() => setShowLibrary(false)}
        />
      )}
      {showMovingLoads && (
        <MovingLoadsPanel
          elements={elements}
          path={movingLoadsPath}
          trains={trains}
          combinations={combinations}
          setPath={setMovingLoadsPath}
          setTrains={setTrains}
          setCombinations={setCombinations}
          formErrors={movingLoadsFormErrors}
          onClose={() => setShowMovingLoads(false)}
        />
      )}
      {showViewOptions && (
        <ViewOptionsPanel
          snapSize={snapSize}
          setSnapSize={setSnapSize}
          gridSpacingX={gridSpacingX}
          setGridSpacingX={setGridSpacingX}
          gridSpacingY={gridSpacingY}
          setGridSpacingY={setGridSpacingY}
          viewScales={viewScales}
          setViewScales={setViewScales}
          displacementUnit={displacementUnit}
          setDisplacementUnit={setDisplacementUnit}
          onClose={() => setShowViewOptions(false)}
        />
      )}
    </CanvasWrapper>
  )
}
